/* ============================================================
   RWDY Theme v57 — theme.js
   All interactions: header, cart drawer, search, wishlist,
   mobile nav, countdown, carousel, stock indicator, notify me,
   toasts, back-to-top, newsletter popup, share button, fit check.
   ============================================================ */

'use strict';

/* ── Utilities ──────────────────────────────────────────────── */
function $(sel, ctx = document) { return ctx.querySelector(sel); }
function $$(sel, ctx = document) { return [...ctx.querySelectorAll(sel)]; }
function qs(id) { return document.getElementById(id); }

function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = parseInt(cents.replace(/[,.]/g, ''));
  // 18 Sep 2026: this produced "Rs. 1190.00" — no thousands separator — while Liquid's `| money`
  // filter renders "Rs. 1,190.00". Both appeared on the same page, which is two of the three price
  // formats the UX audit flagged. Group Indian-style (en-IN gives 1,190 and 1,00,000 correctly)
  // so anything priced in JS matches anything priced in Liquid.
  const value = (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmt = format || window.RWDY.moneyFormat || 'Rs. {{amount}}';
  return fmt.replace(/\{\{[\s]*amount[\s]*\}\}/, value)
            .replace(/\{\{[\s]*amount_no_decimals[\s]*\}\}/, Math.round(cents / 100))
            .replace(/\{\{[\s]*amount_with_comma_separator[\s]*\}\}/, value.replace('.', ','))
            .replace(/\{\{[\s]*amount_no_decimals_with_comma_separator[\s]*\}\}/, Math.round(cents / 100).toString().replace('.', ','));
}

/* ── iOS scroll lock ────────────────────────────────────────── */
let _scrollY = 0;
function lockBody() {
  _scrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${_scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
}
function unlockBody() {
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  window.scrollTo(0, _scrollY);
}

/* ============================================================
   HEADER — hide on scroll down, reveal on scroll up
   ============================================================ */
function initHeader() {
  const header = qs('site-header') || $('.site-header');
  if (!header) return;
  let lastY = 0;
  let ticking = false;

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const headerH = header.offsetHeight;
        if (y > headerH && y > lastY) {
          header.classList.add('header--hidden');
        } else {
          header.classList.remove('header--hidden');
        }
        lastY = y;
        ticking = false;
      });
      ticking = true;
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true });
}

/* ============================================================
   CART DRAWER
   ============================================================ */
let cartDrawerOpen = false;

function initCartDrawer() {
  const drawer = qs('cart-drawer') || $('#cart-drawer');
  const overlay = qs('cart-overlay') || $('#cart-overlay');
  const openBtn = qs('cart-open-btn') || $('#cart-open-btn');
  const closeBtn = qs('cart-drawer-close') || $('#cart-drawer-close');
  const mobileBtn = qs('mobile-cart-btn') || $('#mobile-cart-btn');

  function openCart() {
    cartDrawerOpen = true;
    if (drawer) drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    lockBody();
    refreshCartDrawer();
    // Re-pick the "You might also like" item each time the drawer opens, so it
    // reflects the current cart (and never suggests something already in it).
    if (typeof initCartUpsell === 'function') initCartUpsell();
  }
  function closeCart() {
    cartDrawerOpen = false;
    if (drawer) drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    unlockBody();
  }

  if (openBtn) openBtn.addEventListener('click', openCart);
  if (mobileBtn) mobileBtn.addEventListener('click', openCart);
  if (closeBtn) closeBtn.addEventListener('click', closeCart);
  if (overlay) overlay.addEventListener('click', closeCart);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && cartDrawerOpen) closeCart();
  });

  // Listen for custom cart-open event from product cards
  document.addEventListener('rwdy:open-cart', openCart);
}

/* ============================================================
   PAIR OFFER — "Add one more" nudge in the cart drawer (13 Sep 2026)
   Pool = /collections/the-edit (auto-curated slow movers, tag slow-mover, refreshed
   daily by the restock job). Shopify's automatic BXGY discount takes 30% off ONE
   edit item when the cart holds at least one other item; free delivery at ₹2,999 is
   the store's existing shipping rule. This block only suggests and explains; the
   prices it shows are exactly what Shopify will charge (prepaid — GoKwik gates COD).
   ============================================================ */
const PAIR = { handle: 'the-edit', pct: 0.30, freeAt: 299900, code: 'PAIR30', pool: null, loading: null, applied: null };
/* The 30% is the discount CODE PAIR30, applied by GoKwik at checkout (prepaid only, no COD). */
/* 21 Sep 2026: the site no longer puts PAIR30 into the Shopify cart. When it did, Shopify's BXGY
   PAIR30 used one ladder piece as its "buy" item, so that piece lost its Pick 2/3/4 discount at
   GoKwik checkout (tested: Rs. 5,439.80 instead of Rs. 5,139.95). GoKwik auto-applies PAIR30 itself
   when the cart qualifies (Customise UI > Coupons > Auto Apply, exact code PAIR30), and then the
   ladder stays on every piece (tested: Rs. 5,139.95). So the site only sets the cart attribute,
   and removes a PAIR30 left in a cart or cookie by the older theme. */
function applyPairCode(on) {
  if (PAIR.applied === on) return;
  PAIR.applied = on;
  fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { pair_offer: on ? PAIR.code : '' } }) }).catch(function () {});
}
let _pairStaleCleared = false;
function clearStalePairCode(cart) {
  try {
    ['', '; domain=.' + location.hostname.replace(/^www\./, ''), '; domain=' + location.hostname].forEach(function (d) {
      if (/(^|;\s*)discount_code=PAIR30(;|$)/i.test(document.cookie))
        document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' + d;
    });
  } catch (e) {}
  if (_pairStaleCleared || !cart || !cart.discount_codes) return;
  const codes = cart.discount_codes.map(function (d) { return d.code; });
  if (!codes.some(function (c) { return String(c).toUpperCase() === PAIR.code; })) return;
  _pairStaleCleared = true;
  const keep = codes.filter(function (c) { return String(c).toUpperCase() !== PAIR.code; });
  fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ discount: keep.join(',') }) })
    .then(function () {
      // The cart page prices were rendered with the old code in place: reload once to show the real ones.
      if (/^\/cart\/?$/.test(location.pathname)) { location.reload(); return; }
      if (typeof refreshCartDrawer === 'function' && cartDrawerOpen) refreshCartDrawer();
    })
    .catch(function () {});
}
document.addEventListener('DOMContentLoaded', function () {
  fetch('/cart.js').then(function (r) { return r.json(); }).then(clearStalePairCode).catch(function () {});
});
function loadPairPool() {
  if (PAIR.pool) return Promise.resolve(PAIR.pool);
  if (PAIR.loading) return PAIR.loading;
  PAIR.loading = fetch('/collections/' + PAIR.handle + '/products.json?limit=50')
    .then(r => r.ok ? r.json() : { products: [] })
    .then(j => {
      PAIR.pool = (j.products || []).map(p => {
        const avail = (p.variants || []).filter(v => v.available);
        const price = avail.length ? Math.min.apply(null, avail.map(v => Math.round(parseFloat(v.price) * 100))) : NaN;
        return { id: p.id, handle: p.handle, title: p.title, image: (p.images && p.images[0] && p.images[0].src) || '', price: price };
      }).filter(p => isFinite(p.price) && p.image);
      return PAIR.pool;
    }).catch(() => { PAIR.pool = []; return PAIR.pool; });
  return PAIR.loading;
}
function shortTitle(t) { return String(t).replace(/^RWDY\s+/i, '').replace(/\s*\|.*$/, '').replace(/\s+0\d$/, ''); }

/* One seed per page load: the pair cards must not reshuffle under the customer's hand every
   time the cart drawer re-renders, but they should differ from visit to visit so the whole
   Edit gets shown rather than the same three pieces forever. */
let _pairSeed = null;
function pairSeed() {
  if (_pairSeed === null) _pairSeed = (Math.floor(Math.random() * 0x7ffffffe) + 1) >>> 0;
  return _pairSeed;
}
/* Fisher-Yates driven by a small deterministic PRNG (mulberry32), so the same seed always
   yields the same order. Returns a new array; the input is untouched. */
function rwdyShuffle(list, seed) {
  const a = list.slice();
  let s = seed >>> 0;
  const rand = () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = a[i]; a[i] = a[j]; a[j] = tmp;
  }
  return a;
}
async function renderPairNudge(cart) {
  const lines = document.getElementById('drawer-lines');
  if (!lines) return;
  let host = document.getElementById('pair-nudge');
  if (!host) { host = document.createElement('div'); host.id = 'pair-nudge'; host.className = 'pair-nudge'; lines.insertAdjacentElement('afterend', host); }
  // setCodNote is declared below; function declarations hoist, so these early exits can use it.
  clearStalePairCode(cart);
  if (!cart || cart.item_count === 0) { host.innerHTML = ''; host.style.display = 'none'; setCodNote(false); return; }
  const pool = await loadPairPool();
  if (!pool.length) { host.innerHTML = ''; host.style.display = 'none'; setCodNote(false); return; }
  const poolIds = new Set(pool.map(p => p.id));
  const inCartEdit = cart.items.filter(i => poolIds.has(i.product_id));
  const applied = cart.item_count >= 2 && inCartEdit.length >= 1;
  const gap = PAIR.freeAt - cart.total_price;               // paise still needed for free delivery
  // 18 Sep 2026: this hardcoded "₹" while the rest of the store renders "Rs. " (the shop's
  // money_format). Same page, two currency symbols — the third price format in the audit.
  // Take the symbol from the shop setting instead, so this follows automatically if the store
  // ever switches to ₹. Deliberately rounded with no decimals: this is promotional copy
  // ("Rs. 807 off your 2nd piece"), where "Rs. 807.00" reads worse and buys nothing.
  const moneyPrefix = ((window.RWDY && window.RWDY.moneyFormat) || 'Rs. {{amount}}')
    .split('{{')[0].trim();
  const rs = n => moneyPrefix + (moneyPrefix.endsWith('.') ? ' ' : '') +
    Math.round(n / 100).toLocaleString('en-IN');
  host.style.display = '';

  // The cart drawer's standing note says "COD available", which flatly contradicts the pair
  // offer's "not available on Cash on Delivery" sitting a few pixels below it. Both were true
  // in isolation — COD is fine on most orders, just not this one — but together they read as
  // the site not knowing its own rules. Keep the note honest for the cart in front of them.
  // (17 Sep 2026, flagged in the UX audit as issue #10.)
  function setCodNote(pairOn) {
    document.querySelectorAll('.cart-drawer__note, .order-summary__note').forEach(function (el) {
      if (!el.dataset.codBase) el.dataset.codBase = el.textContent;
      var base = el.dataset.codBase.replace(/\s*·\s*COD available\s*$/i, '');
      el.textContent = pairOn ? base + ' · Pay online for the pair price' : el.dataset.codBase;
    });
  }

  if (applied) {
    applyPairCode(true);
    setCodNote(true);
    // BXGY discounts the eligible (cheapest) edit item; codes are settled at checkout, so estimate here
    const cheapest = Math.min.apply(null, inCartEdit.map(i => i.price));
    const saved = Math.round(cheapest * PAIR.pct);
    host.innerHTML = `
      <div class="pair-nudge__box pair-nudge__box--done">
        <div class="pair-nudge__h">✓ Pair offer on — <b>${rs(saved)} off</b> your 2nd piece at checkout</div>
        <div class="pair-nudge__s">${gap <= 0 ? 'Delivery on this order is <b>free</b>.' : 'Add ' + rs(gap) + ' more for free delivery.'} Pay online (UPI / card) to keep the pair price — not available on Cash on Delivery.</div>
      </div>`;
    return;
  }
  applyPairCode(false);
  setCodNote(false);   // no pair in the cart -> COD really is available, so say so again

  // Suggestions: not already in cart, rotated so the whole Edit gets airtime (15 Sep 2026).
  // This used to sort cheapest-first and slice(0,3), which meant the same three cheap pieces
  // were shown to every customer forever — 3,100+ units across the other fifteen products,
  // including the deepest piles, were never once put in front of anyone. Now the candidates
  // are shuffled, and when the cart is short of free delivery one slot is reserved for a piece
  // that actually closes the gap (picked at random among those that do, since any of them
  // unlocks it). The shuffle is seeded once per page load, so the cards stay put while the
  // customer is looking at them — the drawer re-renders on every cart change — and a different
  // set comes up on the next visit.
  const inCart = new Set(cart.items.map(i => i.product_id));
  const cands = pool.filter(p => !inCart.has(p.id)).map(p => ({ ...p, now: Math.round(p.price * (1 - PAIR.pct)) }));
  const shuffled = rwdyShuffle(cands, pairSeed());
  let picks;
  if (gap > 0) {
    const closers = shuffled.filter(p => p.now >= gap);
    picks = closers.length
      ? [closers[0]].concat(shuffled.filter(p => p.id !== closers[0].id)).slice(0, 3)
      : shuffled.slice(0, 3);
  } else {
    picks = shuffled.slice(0, 3);
  }
  if (!picks.length) { host.innerHTML = ''; host.style.display = 'none'; return; }
  const pct = Math.min(100, Math.round((cart.total_price / PAIR.freeAt) * 100));
  const shipLine = gap > 0
    ? `<div class="pair-nudge__prog"><i style="width:${pct}%"></i></div><div class="pair-nudge__s">${rs(cart.total_price)} of ${rs(PAIR.freeAt)} for free delivery · you're <b>${rs(gap)}</b> away</div>`
    : `<div class="pair-nudge__s">Delivery is already free on this order.</div>`;
  host.innerHTML = `
    <div class="pair-nudge__box">
      <div class="pair-nudge__h">Add one more piece — <b>30% off it</b>${gap > 0 ? ', and your delivery turns free.' : '.'}</div>
      ${shipLine}
      <div class="pair-nudge__row">
        ${picks.map(p => `
          <button type="button" class="pair-nudge__card" data-quick-view="${p.handle}" data-pair-offer="1" aria-label="Add ${p.title} at 30% off">
            <img src="${p.image.replace(/\.(jpg|jpeg|png|webp)(\?.*)?$/i, '_240x300.$1$2')}" alt="" loading="lazy">
            <span class="pair-nudge__nm">${shortTitle(p.title)}</span>
            <span class="pair-nudge__pp"><s>${rs(p.price)}</s> <b>${rs(p.now)}</b></span>
            <span class="pair-nudge__add">Add</span>
          </button>`).join('')}
      </div>
      <div class="pair-nudge__fine">30% comes off at checkout when you pay online (not on COD). <a href="/collections/${PAIR.handle}">See the whole edit →</a></div>
    </div>`;
}

async function refreshCartDrawer() {
  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    renderCartDrawer(cart);
  } catch (e) {
    console.error('Cart fetch failed', e);
  }
}

function renderCartDrawer(cart) {
  const countEl = qs('drawer-cart-count') || $('#drawer-cart-count');
  const emptyEl = qs('drawer-empty-state') || $('#drawer-empty-state');
  const linesEl = qs('drawer-lines') || $('#drawer-lines');
  const subtotalEl = qs('drawer-subtotal') || $('#drawer-subtotal');
  const badgeEl = qs('cart-count-badge') || $('#cart-count-badge');

  if (countEl) countEl.textContent = '· ' + cart.item_count;
  if (badgeEl) badgeEl.textContent = cart.item_count;
  if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

  // Update global count
  window.RWDY.cartCount = cart.item_count;

  if (emptyEl) emptyEl.style.display = cart.item_count === 0 ? 'block' : 'none';

  if (!linesEl) return;

  if (cart.item_count === 0) {
    linesEl.innerHTML = '';
    renderPairNudge(cart);
    return;
  }

  linesEl.innerHTML = cart.items.map(item => `
    <div class="cart-drawer__line" data-line-key="${item.key}">
      <img src="${item.image ? item.image.replace(/\.(jpg|png|webp)/, '_240x320.$1') : ''}" 
           alt="${item.product_title}" 
           width="72" height="92">
      <div class="cart-drawer__line-info">
        <div class="cart-drawer__line-name">${item.product_title}</div>
        <div class="cart-drawer__line-price">${item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title + ' · ' : ''}${formatMoney(item.price)}</div>
        <div class="cart-drawer__line-actions">
          <div class="qty-stepper">
            <button class="qty-stepper__btn" data-action="dec" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Decrease">−</button>
            <span class="qty-stepper__val">${item.quantity}</span>
            <button class="qty-stepper__btn" data-action="inc" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Increase">+</button>
          </div>
          <button class="cart-drawer__remove-btn" data-key="${item.key}">Remove</button>
        </div>
      </div>
    </div>
  `).join('');

  // Bind qty/remove events
  linesEl.querySelectorAll('.qty-stepper__btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const key = btn.dataset.key;
      const qty = parseInt(btn.dataset.qty);
      const action = btn.dataset.action;
      const newQty = action === 'inc' ? qty + 1 : Math.max(0, qty - 1);
      await updateCartLine(key, newQty);
    });
  });
  linesEl.querySelectorAll('.cart-drawer__remove-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      await updateCartLine(btn.dataset.key, 0);
    });
  });
  renderPairNudge(cart);
  paintCartFitLines();
}

/* 21 Oct 2026 — garment measurements inside the cart drawer.

   The drawer is built from /cart.js, which carries no metafields, so the size
   chart is out of reach here. Instead Liquid renders the lines through the
   Section Rendering API and this moves them into place.

   Deliberately fire-and-forget: every failure path (fetch error, missing
   section, no matching key, product without a chart) ends with the drawer
   exactly as it was. The drawer is the last screen before payment and must
   never depend on this. */
let cartFitPending = false;
async function paintCartFitLines() {
  const linesEl = qs('drawer-lines') || $('#drawer-lines');
  if (!linesEl || cartFitPending) return;
  cartFitPending = true;
  try {
    const res = await fetch('/?section_id=cart-fit-lines', { cache: 'no-store' });
    if (!res.ok) return;
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    linesEl.querySelectorAll('.cart-drawer__line').forEach(row => {
      const key = row.getAttribute('data-line-key');
      if (!key) return;
      row.querySelectorAll('.cart-fit-line').forEach(n => n.remove());
      const src = doc.querySelector('[data-fit-for="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"] .cart-fit-line');
      if (!src) return;
      const info = row.querySelector('.cart-drawer__line-info');
      const price = row.querySelector('.cart-drawer__line-price');
      if (!info) return;
      if (price && price.nextSibling) info.insertBefore(src.cloneNode(true), price.nextSibling);
      else info.appendChild(src.cloneNode(true));
    });
  } catch (e) {
    /* drawer stays as rendered */
  } finally {
    cartFitPending = false;
  }
}

async function updateCartLine(key, qty) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    const cart = await res.json();
    // 19 Sep 2026: Shopify returns an error object here (e.g. stock limit reached)
    // instead of a cart when the change can't be applied. Guard against rendering
    // that as if it were a cart, which produced "UNDEFINED" / "Rs. NaN" in the drawer.
    if (!res.ok || typeof cart.item_count === 'undefined') {
      showToast(cart.message || cart.description || 'Could not update quantity');
      await refreshCartDrawer();
      return;
    }
    renderCartDrawer(cart);
  } catch (e) {
    console.error('Cart update failed', e);
  }
}

async function addToCart(variantId, qty = 1, openDrawer = true) {
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    });
    await res.json();
    await refreshCartDrawer();
    if (openDrawer) {
      document.dispatchEvent(new CustomEvent('rwdy:open-cart'));
    }
    showToast('Added to cart');
  } catch (e) {
    showToast('Could not add to cart');
  }
}

/* ============================================================
   SEARCH
   ============================================================ */
let searchOpen = false;

function initSearch() {
  const overlay = qs('search-overlay') || $('#search-overlay');
  const openBtn = qs('search-open-btn') || $('#search-open-btn');
  const closeBtn = qs('search-close-btn') || $('#search-close-btn');
  const input = qs('search-input') || $('#search-input');
  const results = qs('search-results') || $('#search-results');
  const resultsList = qs('search-results-list') || $('#search-results-list');
  const empty = qs('search-empty') || $('#search-empty');   // browse / popular chips (18 Sep 2026)
  const mobileBtn = qs('mobile-search-btn') || $('#mobile-search-btn');

  let searchTimeout;

  function openSearch() {
    searchOpen = true;
    if (overlay) overlay.classList.add('is-open');
    if (empty) empty.classList.add('is-open');   // box starts empty, so chips lead the way in
    setTimeout(() => { if (input) input.focus(); }, 50);
  }
  function closeSearch() {
    searchOpen = false;
    if (overlay) overlay.classList.remove('is-open');
    if (results) results.classList.remove('is-open');
    if (input) input.value = '';
    if (empty) empty.classList.remove('is-open');
  }

  if (openBtn) openBtn.addEventListener('click', openSearch);
  if (mobileBtn) mobileBtn.addEventListener('click', openSearch);
  if (closeBtn) closeBtn.addEventListener('click', closeSearch);

  if (input) {
    input.addEventListener('input', () => {
      clearTimeout(searchTimeout);
      const q = input.value.trim();
      // 18 Sep 2026: show the browse/popular chips only while the box is empty, so they give a
      // starting point without ever sitting on top of real results.
      if (empty) empty.classList.toggle('is-open', q.length === 0);
      if (q.length < 2) {
        if (results) results.classList.remove('is-open');
        return;
      }
      searchTimeout = setTimeout(() => fetchSearchResults(q), 300);
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && input.value.trim()) {
        window.location = `/search?q=${encodeURIComponent(input.value.trim())}&type=product`;
      }
      if (e.key === 'Escape') closeSearch();
    });
  }

  async function fetchSearchResults(q) {
    try {
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`);
      const data = await res.json();
      const products = data.resources.results.products || [];
      if (!resultsList || !results) return;
      if (products.length === 0) {
        resultsList.innerHTML = `<div style="font-size:13px;color:#8a8a85;padding:8px 0">No products found. Try "shirt", "cargo", "hoodie"…</div>`;
      } else {
        resultsList.innerHTML = products.map(p => `
          <a href="${p.url}" class="header__search-result-item">
            <img src="${p.image ? p.image.replace(/\.(jpg|png|webp)/, '_80x100.$1') : ''}" alt="${p.title}" width="40" height="50">
            <span class="header__search-result-name">${p.title}</span>
            <span class="header__search-result-price">${formatMoney(p.price)}</span>
          </a>
        `).join('');
      }
      results.classList.add('is-open');
    } catch (e) {
      console.error('Search failed', e);
    }
  }

  // Close when clicking outside the overlay.
  //
  // BUGFIX: this used to be `e.target !== openBtn`, which had two holes and
  // between them made the mobile Search button do nothing at all:
  //   1. It never excluded the bottom-nav trigger (#mobile-search-btn), so the
  //      very click that opened search bubbled up here and closed it again.
  //   2. It compared the target by identity, but both triggers contain child
  //      <span> elements, so a real tap reports the span as e.target, not the
  //      button. Even the desktop button would break if it gained an icon.
  // closest() covers the buttons and anything inside them.
  document.addEventListener('click', (e) => {
    if (!searchOpen || !overlay) return;
    if (overlay.contains(e.target)) return;
    const t = e.target instanceof Element ? e.target : null;
    // The chips panel and the results list are siblings of the overlay, not children, so
    // overlay.contains() misses them. Without this, a tap on a chip closes search on the way out.
    if (t && t.closest('#search-empty, #search-results')) return;
    if (t && t.closest('#search-open-btn, #mobile-search-btn')) return;
    closeSearch();
  });
}

/* ============================================================
   WISHLIST (localStorage)
   ============================================================ */
const WISH_KEY = 'rwdy_wishlist';

function getWishlist() {
  try { return JSON.parse(localStorage.getItem(WISH_KEY)) || []; } catch { return []; }
}
function saveWishlist(list) {
  localStorage.setItem(WISH_KEY, JSON.stringify(list));
}
function isWishlisted(id) {
  return getWishlist().includes(String(id));
}
function toggleWishlist(id, title) {
  const list = getWishlist();
  const sid = String(id);
  const idx = list.indexOf(sid);
  if (idx === -1) {
    list.push(sid);
    showToast('Added to wishlist');
  } else {
    list.splice(idx, 1);
    showToast('Removed from wishlist');
  }
  saveWishlist(list);
  updateWishBadge();
  return idx === -1;
}

function updateWishBadge() {
  const count = getWishlist().length;
  const badge = qs('wish-badge') || $('#wish-badge');
  if (!badge) return;
  badge.textContent = count;
  badge.style.display = count > 0 ? 'inline-flex' : 'none';
}

function initWishlist() {
  updateWishBadge();

  // Wish button on cards/PDP
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wish-id]');
    if (!btn) return;
    e.preventDefault();
    const id = btn.dataset.wishId;
    const added = toggleWishlist(id);
    // Update all buttons for same product
    document.querySelectorAll(`[data-wish-id="${id}"]`).forEach(b => {
      b.classList.toggle('is-wishlisted', added);
      b.textContent = added ? '♥' : '♡';
    });
  });

  // Wishlist nav redirect
  const navBtn = qs('header-wish-btn') || $('#header-wish-btn');
  const mobileWishBtn = qs('mobile-wish-nav-btn') || $('#mobile-wish-nav-btn');
  if (navBtn) navBtn.addEventListener('click', () => window.location = '/pages/wishlist');
  if (mobileWishBtn) mobileWishBtn.addEventListener('click', () => window.location = '/pages/wishlist');
}

/* ============================================================
   MOBILE NAV
   ============================================================ */
function initMobileNav() {
  const overlay = qs('mobile-overlay') || $('#mobile-overlay');
  const drawer  = qs('mobile-drawer')  || $('#mobile-drawer');
  const menuBtn = qs('mobile-menu-btn') || $('#mobile-menu-btn');

  function openDrawer() {
    if (drawer)  drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    if (menuBtn) menuBtn.setAttribute('aria-expanded', 'true');
    lockBody();
  }

  function closeDrawer() {
    if (drawer)  drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    if (menuBtn) menuBtn.setAttribute('aria-expanded', 'false');
    unlockBody();
  }

  if (menuBtn)  menuBtn.addEventListener('click', openDrawer);
  if (overlay)  overlay.addEventListener('click', closeDrawer);

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) closeDrawer();
  });
}

/* ============================================================
   COUNTDOWN TIMER
   ============================================================ */
function initCountdown(targetDate) {
  const units = {
    d: document.getElementById('countdown-days'),
    h: document.getElementById('countdown-hours'),
    m: document.getElementById('countdown-minutes'),
    s: document.getElementById('countdown-seconds'),
  };
  if (!units.d && !units.h && !units.m && !units.s) return;

  const target = targetDate ? new Date(targetDate) : new Date(Date.now() + 14 * 24 * 3600 * 1000);

  function tick() {
    const diff = target - Date.now();
    if (diff <= 0) {
      Object.values(units).forEach(el => { if (el) el.textContent = '00'; });
      return;
    }
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    if (units.d) units.d.textContent = String(d).padStart(2, '0');
    if (units.h) units.h.textContent = String(h).padStart(2, '0');
    if (units.m) units.m.textContent = String(m).padStart(2, '0');
    if (units.s) units.s.textContent = String(s).padStart(2, '0');
  }
  tick();
  setInterval(tick, 1000);
}

/* ============================================================
   CAROUSEL (Rwdy Frames)
   ============================================================ */
function initCarousel(containerSelector = '.rwdy-frames') {
  // 17 Oct 2026 — RWDY Looks is no longer a carousel.
  //
  // It was an auto-advancing filmstrip: four cards on screen, the rest off to
  // the right, sliding every 4.2 seconds. Two things followed from that, and
  // both cost sales. A shopper reading one look had it slide away mid-read, so
  // no single look was ever reliably on screen; and the price of a look only
  // existed inside the panel, which only opened on a click nobody makes at a
  // moving target. The strip is now a plain grid (see theme.css) and every
  // card shows its own total up front, worked out in Liquid.
  //
  // All of it is gone: the track transform, --frames-shown width maths, slot
  // counting, arrows, dots, swipe, the AUTOPLAY timer and the background-image
  // swap that followed the active card. What remains below is the shop-the-look
  // panel, which was never the problem. The function keeps its old name because
  // the init list at the bottom of this file calls it by that name.
  const container = document.querySelector(containerSelector);
  if (!container) return;

  const cards = $$('[data-slide]', container);
  if (cards.length === 0) return;

  // The panel used to pause and resume the filmstrip around itself. With no
  // timer left there is nothing to pause, so these are no-ops rather than
  // deleted call sites — fewer edits inside working panel code.
  function startAutoplay() {}
  function stopAutoplay() {}


  // ---- shop-the-look panel (13 Aug) ----
  // A frame that carries products opens the panel; frames without product
  // data behave as before.
  const panel = container.querySelector('[data-look-panel]');
  if (panel) {
    const itemsEl = panel.querySelector('[data-look-items]');
    const addAllBtn = panel.querySelector('[data-look-add-all]');
    const money = v => 'Rs. ' + (v / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 });
    let currentLook = [];

    // resolve(): maps a product's chosen option values to a concrete
    // variant. A null slot in a variant's opts always matches, which covers
    // products with fewer than three options.
    function resolve(p, choices) {
      return p.variants.find(v =>
        v.opts.every((o, i) => o === null || o === (choices[i] !== undefined ? choices[i] : o)));
    }
    function hasRealOptions(p) {
      return !(p.optionNames.length === 1 && p.optionNames[0] === 'Title');
    }
    function esc(t) {
      return String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
    }

    function openLook(products) {
      currentLook = products;
      // default every item to its first available variant's options
      products.forEach(p => {
        const first = p.variants.find(v => v.available) || p.variants[0];
        p.choices = first ? first.opts.slice(0, p.optionNames.length) : [];
      });

      itemsEl.innerHTML = products.map((p, pi) => {
        const v = resolve(p, p.choices);
        const selects = hasRealOptions(p) ? p.optionNames.map((name, oi) => {
          const seen = [];
          p.variants.forEach(vv => {
            if (vv.opts[oi] !== null && seen.indexOf(vv.opts[oi]) === -1) seen.push(vv.opts[oi]);
          });
          if (seen.length < 2) return '';        // one value only: nothing to pick
          return `<select class="look-item__opt" data-pi="${pi}" data-oi="${oi}" aria-label="${esc(name)}">` +
            seen.map(val =>
              `<option value="${esc(val)}"${val === p.choices[oi] ? ' selected' : ''}>${esc(val)}</option>`
            ).join('') + '</select>';
        }).join('') : '';
        const ok = v && v.available;
        return `
        <div class="look-item">
          <img class="look-item__img" src="${p.image || ''}" alt="" loading="lazy">
          <div class="look-item__body">
            <a class="look-item__name" href="${p.url}">${esc(p.title)}</a>
            ${selects ? `<div class="look-item__opts">${selects}</div>` : ''}
            <div class="look-item__price" data-look-price="${pi}">${money(v ? v.price : p.price)}</div>
          </div>
          <button class="look-item__add" data-look-add="${pi}" type="button"${ok ? '' : ' disabled'}>${ok ? 'Add' : 'Sold out'}</button>
        </div>`;
      }).join('');

      syncAddAll();
      panel.hidden = false;
      document.body.style.overflow = 'hidden';
      stopAutoplay();
    }

    /* 17 Oct 2026: the panel now shows what the whole look costs, and what the
       bundle ladder takes off it. The ladder (Pick 2 = 15%, Pick 3 = 25%,
       Pick 4+ = 30%) is a Shopify automatic discount scoped to the
       bundle-eligible collection, so the saving is worked out ONLY over items
       carrying bundleEligible from the Liquid. A look of Unafraid pieces shows
       a total and no saving, which is what checkout will actually do. */
    const BUNDLE_TIERS = { 2: 0.15, 3: 0.25 };
    function bundleRate(n) {
      if (n >= 4) return 0.30;
      return BUNDLE_TIERS[n] || 0;
    }

    const totalEl = panel.querySelector('[data-look-total]');
    const wasEl = panel.querySelector('[data-look-was]');
    const nowEl = panel.querySelector('[data-look-now]');
    const saveEl = panel.querySelector('[data-look-save]');

    function syncAddAll() {
      const buyable = currentLook.filter(p => {
        const v = resolve(p, p.choices);
        return v && v.available;
      });
      addAllBtn.hidden = buyable.length < 2;
      addAllBtn.disabled = false;
      addAllBtn.textContent = `Add entire look to cart (${buyable.length})`;

      if (!totalEl) return;
      if (buyable.length < 2) { totalEl.hidden = true; return; }

      let gross = 0, eligibleSubtotal = 0, eligibleCount = 0;
      buyable.forEach(p => {
        const v = resolve(p, p.choices);
        const price = v ? v.price : p.price;
        gross += price;
        if (p.bundleEligible) { eligibleSubtotal += price; eligibleCount++; }
      });

      const rate = bundleRate(eligibleCount);
      const saving = Math.round(eligibleSubtotal * rate);
      const net = gross - saving;

      totalEl.hidden = false;
      nowEl.textContent = money(net);
      if (saving > 0) {
        wasEl.hidden = false;
        wasEl.textContent = money(gross);
        saveEl.hidden = false;
        /* Say the real count, not the tier name: with 5 eligible pieces
           "Pick 4 saves 30%" reads like it is about to drop one. */
        saveEl.textContent = `${eligibleCount} pieces save ${Math.round(rate * 100)}% — ${money(saving)} off, applied in the bag`;
      } else {
        wasEl.hidden = true;
        saveEl.hidden = true;
      }
    }

    function syncItem(pi) {
      const p = currentLook[pi];
      const v = resolve(p, p.choices);
      const priceEl = itemsEl.querySelector(`[data-look-price="${pi}"]`);
      const addBtn = itemsEl.querySelector(`[data-look-add="${pi}"]`);
      if (priceEl) priceEl.textContent = money(v ? v.price : p.price);
      if (addBtn) {
        const ok = v && v.available;
        addBtn.disabled = !ok;
        addBtn.textContent = ok ? 'Add' : 'Unavailable';
      }
      syncAddAll();
    }

    function closeLook() {
      panel.hidden = true;
      document.body.style.overflow = '';
      startAutoplay();
    }

    // BUGFIX kept from live: the collection here is `cards`, not `slides`.
    // (`slides` threw a ReferenceError during initCarousel and took the drop
    // countdown down with it.)
    cards.forEach(card => {
      const dataEl = card.querySelector('[data-look-products]');
      if (!dataEl) return;
      card.style.cursor = 'pointer';
      card.addEventListener('click', () => {
        try { openLook(JSON.parse(dataEl.textContent)); }
        catch (e) { /* malformed JSON: frame simply is not shoppable */ }
      });
    });

    panel.querySelectorAll('[data-look-close]').forEach(el =>
      el.addEventListener('click', closeLook));
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !panel.hidden) closeLook();
    });

    itemsEl.addEventListener('change', e => {
      const sel = e.target.closest('.look-item__opt');
      if (!sel) return;
      const pi = +sel.dataset.pi, oi = +sel.dataset.oi;
      currentLook[pi].choices[oi] = sel.value;
      syncItem(pi);
    });

    itemsEl.addEventListener('click', e => {
      const btn = e.target.closest('[data-look-add]');
      if (!btn || btn.disabled) return;
      const p = currentLook[+btn.dataset.lookAdd];
      const v = resolve(p, p.choices);
      if (!v || !v.available) return;
      btn.disabled = true; btn.textContent = 'Added';
      addToCart(v.id, 1, false);
    });

    addAllBtn.addEventListener('click', async () => {
      const items = currentLook
        .map(p => resolve(p, p.choices))
        .filter(v => v && v.available)
        .map(v => ({ id: v.id, quantity: 1 }));
      if (!items.length) return;
      addAllBtn.disabled = true;
      addAllBtn.textContent = 'Adding...';
      try {
        // one request for the whole outfit, at the chosen sizes/colours
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        await refreshCartDrawer();
        closeLook();
        document.dispatchEvent(new CustomEvent('rwdy:open-cart'));
        showToast('Look added to cart');
      } catch (err) {
        showToast('Could not add the look');
      }
      addAllBtn.disabled = false;
    });
  }
}

/* ============================================================
   STOCK INDICATOR
   ============================================================ */
function initStockIndicator() {
  const sizeBtns = $$('.size-btn');
  const indicator = document.querySelector('.stock-indicator');
  if (!sizeBtns.length || !indicator) return;

  sizeBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.classList.contains('is-oos')) return;
      sizeBtns.forEach(b => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');
      const qty = parseInt(btn.dataset.qty) || 99;
      indicator.className = 'stock-indicator';
      indicator.textContent = '';
      setTimeout(() => {
        if (qty === 0) {
          indicator.classList.add('stock-indicator--oos');
          indicator.textContent = 'Out of stock';
        } else if (qty <= 5) {
          indicator.classList.add('stock-indicator--low');
          indicator.textContent = `Only ${qty} left!`;
        } else {
          indicator.classList.add('stock-indicator--in');
          indicator.textContent = 'In stock';
        }
      }, 80);
    });
  });
}

/* ============================================================
   NOTIFY ME (OOS email)
   ============================================================ */
function initNotifyMe() {
  document.addEventListener('submit', async (e) => {
    if (!e.target.matches('.notify-form')) return;
    e.preventDefault();
    const form = e.target;
    const email = form.querySelector('.notify-form__input')?.value;
    if (!email) return;
    // In production, wire to Klaviyo / Omnisend back-in-stock endpoint
    showToast('We\'ll let you know when it\'s back!');
    form.reset();
  });
}

/* ============================================================
   TOAST
   ============================================================ */
function showToast(message) {
  const container = qs('toast-container') || $('#toast-container');
  if (!container) return;
  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.textContent = message;
  container.appendChild(toast);
  setTimeout(() => {
    toast.remove();
  }, 2500);
}

/* ============================================================
   BACK TO TOP
   ============================================================ */
function initBackToTop() {
  const btn = qs('back-to-top') || $('#back-to-top');
  if (!btn) return;
  window.addEventListener('scroll', () => {
    btn.classList.toggle('is-visible', window.scrollY > 700);
  }, { passive: true });
  btn.addEventListener('click', () => {
    // GSAP ScrollTrigger on this page cancels smooth programmatic scrolls,
    // so try smooth first and force an instant jump if it didn't take.
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => { if (window.scrollY > 100) window.scrollTo(0, 0); }, 400);
  });
}

/* ============================================================
   NEWSLETTER POPUP
   ============================================================ */
function initNewsletter() {
  const popup = qs('newsletter-popup') || $('#newsletter-popup');
  if (!popup) return;
  // Never show the "new joiners" offer to a logged-in customer.
  if (window.RWDY && window.RWDY.customerLoggedIn) { popup.remove(); return; }
  // Don't re-show to anyone who has already seen or claimed it (persists across sessions).
  if (localStorage.getItem('rwdy_nl_seen') || sessionStorage.getItem('rwdy_nl_seen')) return;

  const closeBtn = qs('newsletter-close') || $('#newsletter-close');
  const skip = qs('newsletter-skip') || $('#newsletter-skip');
  const form = qs('newsletter-popup-form') || $('#newsletter-popup-form');

  function closePopup() {
    popup.classList.remove('is-open');
    sessionStorage.setItem('rwdy_nl_seen', '1');
    try { localStorage.setItem('rwdy_nl_seen', '1'); } catch (e) {}
  }

  // 17 Sep 2026 — this was a flat setTimeout(..., 6000).
  // The clock started when this script ran, but the Sep audit measured mobile LCP at 11.6s and
  // time-to-interactive at 22.6s, so on a real connection the popup could land on a page the
  // customer had not actually seen yet. They get interrupted before there is anything to be
  // interested in, dismiss it — and because closePopup() remembers that forever in localStorage,
  // the one shot at that signup is spent on someone who never saw a product.
  // It now waits for a sign of genuine interest instead of a clock: half a page of scrolling, or
  // (on pointer devices) the cursor leaving towards the tab bar. A long fallback still catches
  // someone who reads without scrolling, so nobody is missed entirely.
  let popupShown = false;
  function cleanupTriggers() {
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('mouseout', onExitIntent);
    clearTimeout(fallbackTimer);
  }
  function openPopup() {
    if (popupShown) return;
    // Re-check: they may have dismissed it in another tab since this page loaded.
    if (localStorage.getItem('rwdy_nl_seen') || sessionStorage.getItem('rwdy_nl_seen')) { cleanupTriggers(); return; }
    popupShown = true;
    popup.classList.add('is-open');
    cleanupTriggers();
  }
  function onScroll() {
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;                       // page too short to scroll: leave it to the fallback
    if ((window.scrollY || doc.scrollTop) / scrollable >= 0.5) openPopup();
  }
  function onExitIntent(e) {
    // Fires when the pointer leaves the top of the viewport. Meaningless on touch, so it is
    // only bound on hover-capable devices.
    if (e.clientY <= 0 && !e.relatedTarget) openPopup();
  }
  const fallbackTimer = setTimeout(openPopup, 45000);
  window.addEventListener('scroll', onScroll, { passive: true });
  if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
    document.addEventListener('mouseout', onExitIntent);
  }
  if (closeBtn) closeBtn.addEventListener('click', closePopup);
  if (skip) skip.addEventListener('click', closePopup);
  popup.addEventListener('click', (e) => {
    if (e.target === popup) closePopup();
  });
  if (form) {
    const emailInput = form.querySelector('input[type="email"]');
    // strict-enough client-side email format check (real validation/first-time is done by the email app)
    const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = ((emailInput && emailInput.value) || '').trim();
      if (!isValidEmail(email)) {
        if (emailInput) {
          emailInput.setCustomValidity('Please enter a valid email address');
          emailInput.reportValidity();
        }
        showToast('Please enter a valid email address.');
        return;
      }
      if (emailInput) emailInput.setCustomValidity('');

      // capture the subscriber in Shopify (also syncs to the email app) — fire-and-forget
      try {
        const fd = new FormData(form);
        fetch(form.action, { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } });
      } catch (err) { /* non-blocking */ }

      // reveal the code on the spot — reliable, no dependency on email delivery
      const CODE = 'WELCOME10';
      sessionStorage.setItem('rwdy_nl_seen', '1');
    try { localStorage.setItem('rwdy_nl_seen', '1'); } catch (e) {}
      const card = popup.querySelector('.newsletter-popup');
      if (card) {
        card.innerHTML =
          '<button class="newsletter-popup__close" id="newsletter-close-2" aria-label="Close">✕</button>' +
          '<div class="newsletter-popup__kicker">You’re in 🎉</div>' +
          '<h2 class="newsletter-popup__heading">10% off your<br>first order.</h2>' +
          '<p class="newsletter-popup__sub">Enter this code at checkout. It’s yours for your first order.</p>' +
          '<div id="rwdy-code-box" style="display:flex;align-items:center;justify-content:center;gap:10px;margin:14px 0;border:2px dashed currentColor;border-radius:10px;padding:14px 16px;font-weight:800;letter-spacing:2px;font-size:20px;">' +
            '<span id="rwdy-code">' + CODE + '</span>' +
          '</div>' +
          '<button type="button" class="newsletter-popup__submit" id="rwdy-copy-code">Copy code</button>' +
          '<a class="newsletter-popup__skip" id="rwdy-shop-now" href="/collections/all" style="display:inline-block;margin-top:12px;">Shop the drop →</a>';
        const c2 = card.querySelector('#newsletter-close-2');
        if (c2) c2.addEventListener('click', closePopup);
        const copyBtn = card.querySelector('#rwdy-copy-code');
        if (copyBtn) {
          copyBtn.addEventListener('click', () => {
            try { navigator.clipboard && navigator.clipboard.writeText(CODE); } catch (e) {}
            copyBtn.textContent = 'Copied ✓';
          });
        }
      } else {
        showToast('You’re in! Your code is ' + CODE + ', 10% off your first order.');
        closePopup();
      }
    });

    // clear the custom error as the user types
    if (emailInput) {
      emailInput.addEventListener('input', () => emailInput.setCustomValidity(''));
    }
  }
}

/* ============================================================
   SHARE BUTTON
   ============================================================ */
function initShareBtn() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share-url]');
    if (!btn) return;
    const url = btn.dataset.shareUrl || window.location.href;
    const title = btn.dataset.shareTitle || document.title;
    const glyph = btn.querySelector('.share-glyph');

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (err) { /* fallback to clipboard */ }
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    if (glyph) {
      const orig = glyph.textContent;
      glyph.textContent = '✓';
      setTimeout(() => { glyph.textContent = orig; }, 2000);
    }
    showToast('Link copied!');
  });
}

/* ============================================================
   FIT CHECK (upload gated by login)
   ============================================================ */
function initFitCheck() {
  const uploadBtn = document.querySelector('.fit-check__upload');
  const modal = qs('upload-modal') || $('#upload-modal');
  const closeBtn = qs('upload-modal-close') || $('#upload-modal-close');
  const content = document.getElementById('upload-modal-content');

  if (!uploadBtn || !modal) return;

  uploadBtn.addEventListener('click', () => {
    if (window.RWDY.customerLoggedIn) {
      // Show upload form
      if (content) content.innerHTML = `
        <h2 style="font-size:18px;font-weight:900;letter-spacing:0.04em;text-transform:uppercase;margin-bottom:20px">Share Your Fit</h2>
        <form id="fit-upload-form">
          <div style="margin-bottom:14px">
            <label style="font-size:11px;font-weight:700;letter-spacing:0.16em;text-transform:uppercase;color:#8a8a85;display:block;margin-bottom:6px">Your Photo</label>
            <input type="file" accept="image/*" required style="font-size:13px">
          </div>
          <div style="margin-bottom:14px">
            <label style="font-size:11px;font-weight:700;letter-spacing:0.16em;text-transform:uppercase;color:#8a8a85;display:block;margin-bottom:6px">Handle / Instagram</label>
            <input type="text" placeholder="@yourusername" class="form-input" style="width:100%">
          </div>
          <div style="margin-bottom:20px">
            <label style="font-size:11px;font-weight:700;letter-spacing:0.16em;text-transform:uppercase;color:#8a8a85;display:block;margin-bottom:6px">Size Worn</label>
            <select class="form-select" style="margin-bottom:0"><option>XS</option><option>S</option><option selected>M</option><option>L</option><option>XL</option><option>XXL</option></select>
          </div>
          <button type="submit" class="btn btn--solid btn--full">Submit Fit</button>
          <p style="font-size:11.5px;color:#9a9a96;text-align:center;margin-top:10px">Approved fits earn 50 RWDY Club coins.</p>
        </form>
      `;
      document.getElementById('fit-upload-form')?.addEventListener('submit', (e) => {
        e.preventDefault();
        showToast('Fit submitted — pending review!');
        modal.classList.remove('is-open');
      });
    } else {
      // Prompt login
      if (content) content.innerHTML = `
        <h2 style="font-size:18px;font-weight:900;letter-spacing:0.04em;text-transform:uppercase;margin-bottom:14px">Sign In to Share</h2>
        <p style="font-size:13.5px;color:#55554f;line-height:1.7;margin-bottom:24px">Log in or create an account to share your fit and earn RWDY Club coins.</p>
        <a href="/account/login?return_url=${encodeURIComponent(window.location.pathname)}" class="btn btn--solid btn--full" style="display:flex">Log In</a>
        <a href="/account/register" class="btn btn--outline btn--full" style="display:flex;margin-top:10px">Create Account</a>
      `;
    }
    modal.classList.add('is-open');
  });

  if (closeBtn) closeBtn.addEventListener('click', () => modal.classList.remove('is-open'));
  modal.addEventListener('click', (e) => { if (e.target === modal) modal.classList.remove('is-open'); });
}

/* ============================================================
   SPOTTED BY RWDY — hotspot dots
   ============================================================ */
/* ============================================================
   OTHERS ALSO BOUGHT — algorithmic PDP cross-sell (12 Aug)
   ============================================================ */
function initOthersBought() {
  const sec = document.querySelector('[data-others-bought]');
  if (!sec) return;
  const pid = sec.dataset.productId;
  const limit = parseInt(sec.dataset.limit, 10) || 4;
  if (!pid) return;

  // Shopify's own engine: purchase history + similarity. No API key needed,
  // same-origin endpoint, returns full product JSON with prices in the
  // shop currency (paise for INR, hence /100 below).
  fetch(`/recommendations/products.json?product_id=${pid}&limit=${limit}&intent=related`)
    .then(r => r.ok ? r.json() : { products: [] })
    .then(data => {
      const prods = (data.products || []).filter(p => p.available !== false);
      if (!prods.length) return;           // section stays hidden: no noise on new products
      const grid = sec.querySelector('[data-others-bought-grid]');
      const money = v => 'Rs. ' + (v / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 });
      grid.innerHTML = prods.map(p => {
        const img = (p.featured_image || (p.images && p.images[0]) || '');
        const onSale = p.compare_at_price && p.compare_at_price > p.price;
        return `
        <a class="product-card" href="${p.url}">
          <div class="product-card__media">
            ${img ? `<img class="product-card__img" src="${img}${img.includes('?') ? '&' : '?'}width=600" alt="" loading="lazy"
                       onerror="this.src='${img}'">` : ''}
            ${onSale ? '<span class="product-card__badge product-card__badge--sale">SALE</span>' : ''}
          </div>
          <div class="product-card__info">
            <div class="product-card__name">${p.title}</div>
            <div class="product-card__price">
              ${onSale ? `<span class="product-card__compare">${money(p.compare_at_price)}</span>
                          <span class="product-card__sale-price">${money(p.price)}</span>`
                       : money(p.price)}
            </div>
          </div>
        </a>`;
      }).join('');
      sec.hidden = false;
    })
    .catch(() => { /* recommendations are decoration; a failure stays silent */ });
}

/* ============================================================
   COLOUR SWATCHES — filter dots (13 Aug)
   Names are matched lowercase. Values: hex string, [hex, hex] for a
   two-tone split, "print" for patterned goods, "multi" for rainbow.
   Unknown names keep the neutral dot from CSS: nothing breaks when
   merchandising invents a new colour, it just ships uncoloured until
   someone adds a line here.
   ============================================================ */
const RWDY_SWATCHES = {
  "almond":"#efdecd","arctic white":"#f5f7f7","ash grey":"#b2beb5","baby blue":"#89cff0",
  "bandana print mesh":"print","beige":"#d9c7a7","beige washed":"#e3d5bc","black":"#111111",
  "black smoke":"#2e2e2e","blue":"#2e5aac","brick red":"#8e3b2f","brown":"#6b4a2f",
  "burnt red":"#a33327","camo print":"print","cement":"#b7b1a6","charcoal":"#3c3c3c",
  "charcoal wave":"#43464b","classic blue":"#34568b","coastal blue":"#4f7cac",
  "coastal navy":"#2c3e60","cocoa":"#6b4f3a","cool blue":"#7fa8c9","coral":"#ff7f61",
  "cream":"#f3ead3","crimson heat":"#b02330","dark earth":"#4a3b2a","dark grey":"#555555",
  "dark olive":"#4a4a2f","dark yellow":"#d9a400","dawn blue":"#a8c0d6","deep blue":"#1e3a6e",
  "deep cocoa":"#4e3628","deep forest":"#2a4934","deep plum":"#4e2a45","default":"#999999",
  "denim blue":"#3f5e82","desert gold":"#c9a24b","desert sand":"#d8c29d","desert taupe":"#b9a088",
  "dust sage":"#aeb79f","dusty blue":"#7c99b0","dusty plum":"#8a6478","dusty rose":"#c08a8a",
  "evergreen fog":"#95a69b","forest green":"#2f5d3a","gold":"#c9a227","golden hour":"#e0a93e",
  "grape purple":"#5d3b6a","green":"#3e7c4f","grey":"#8c8c8c",
  "grey & blue":["#8c8c8c","#3f5e82"],"grey and blue stripe":["#8c8c8c","#3f5e82"],
  "hunter green":"#33513e","ice fade":"#c7d4de","ice wash":"#cbd6de","ice washed":"#cbd6de",
  "indigo":"#3f4c8c","indigo print":"print","inked":"#22252b","ivory":"#f4efe2",
  "ivory & caramel":["#f4efe2","#b77d4c"],"ivory and caramel stripe":["#f4efe2","#b77d4c"],
  "ivory dune":"#eae0cc","jade":"#4fa083","jet black":"#0b0b0b","khaki":"#a08d5f",
  "lavender":"#b9a7d6","light grey":"#c8c8c8","lime green":"#9ccb3b","lime yellow":"#d8e24c",
  "matcha":"#9db584","matte black":"#1a1a1a","midnight":"#1b2231","midnight black":"#101319",
  "midnight moss":"#33413a","midnight navy":"#1e2a44","moss brown":"#6e6047",
  "moss khaki":"#8b8358","mosswood":"#5e6248","multi":"multi","multi plaid":"print",
  "mustard":"#d2a727","muted mauve":"#b08e9a","navy":"#22355c","navy cire":"#202d4d",
  "neon surge":"#c6ff3d","neon surge & royal crest":["#c6ff3d","#30427a"],"noir":"#14141a",
  "off white":"#efede6","olive":"#6f6b3f","olive fog":"#8c8f76","olive green":"#5c6b3c",
  "orange":"#e87722","orange blast":"#f26b1d","orange peel":"#f28c28","orchid mist":"#c9a8c9",
  "parrot green":"#57b24c","pearl":"#efe9e0","pebble tan":"#c9b8a0","pistachio mist":"#c4d4b0",
  "rage":"#c0272d","raisin brown":"#5a4436","red":"#c22b2b","rose quartz":"#e8b4b8",
  "royal blue":"#2b4c9b","royal crest":"#30427a","sage green":"#9caf88","sand beige":"#dbc6a2",
  "sand print":"print","sandstone":"#c9ae88","sepia":"#704f32","sienna blaze":"#c1633c",
  "sky drift":"#b8d0e0","skyline":"#7ba5c9","slate shadow":"#5c6670","slate wine":"#6e4a55",
  "smoke":"#a9a9a9","steel blue":"#4a6e8a","stone black":"#2b2b28","stone grey":"#9a9a93",
  "storm blue":"#50647d","sunfaded black":"#3a3a38","tarmac grey":"#4c4f52",
  "tiger's eye":"#b5651d","white":"#ffffff","yellow":"#f2c500"
};

function initColorSwatches() {
  document.querySelectorAll('.filter-swatch[data-swatch]').forEach(el => {
    const v = RWDY_SWATCHES[el.dataset.swatch];
    if (!v) return;                                   // neutral dot stays
    if (v === 'print') { el.classList.add('filter-swatch--print'); return; }
    if (v === 'multi') { el.classList.add('filter-swatch--multi'); return; }
    if (Array.isArray(v)) {
      el.style.background = `linear-gradient(135deg, ${v[0]} 0 50%, ${v[1]} 50% 100%)`;
      return;
    }
    el.style.background = v;
  });
}

function initSpotted() {
  // 17 Oct 2026 — the coverflow (pos-* class swaps, hotspot dots, popups,
  // arrows, dots, swipe-to-advance) was deleted with the markup it drove.
  // What is left is one quick-add handler: every piece is now a visible row
  // under its photo, so there is nothing to open, advance or re-centre.
  const grid = document.querySelector('[data-spotted-grid]');
  if (!grid) return;

  grid.addEventListener('click', (e) => {
    const btn = e.target.closest('.spotted-row__add');
    if (!btn || btn.tagName !== 'BUTTON') return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    e.preventDefault();
    addToCart(variantId);
  });
}

/* ============================================================
   FIT PANEL (Product Page) - 21 Oct 2026
   ============================================================
   Returns analysis, 30 days: 76% of return line items are size, and the
   direction tracks the size ordered - XS/S/M come back "too small", L/XXL
   come back "too large". Root cause is that the size letter is not comparable
   across blocks. Full chest at M:

       Duskline muscle tee   38in      <- smallest thing in the catalogue
       Muscle tee            41in
       Polo / Grain henley   46in
       Oversized tee         47.2in    <- what customers think "RWDY M" means
       Cold front flannel    48in      (but shoulder 21in vs the tee's 24.2in)
       Winter hoodie         57in

   All of that already existed in the size_chart metaobjects; it was just
   parked behind a "Size guide" modal. This surfaces it at the one moment it
   changes a decision: when a size button is clicked.

   Everything degrades to nothing if the chart is missing or malformed. A
   product with no chart renders exactly what it rendered before.
   ============================================================ */
function initFitPanel() {
  const panel = document.querySelector('[data-fit-panel]');
  const chart = window.RWDY_SIZE_CHART;
  if (!panel || !chart || !Array.isArray(chart.rows) || !chart.rows.length) return;

  const measureEl = panel.querySelector('[data-fit-measure]');
  const anchorEl = panel.querySelector('[data-fit-anchor]');
  if (!measureEl) return;

  const isBottom = (chart.type === 'Bottoms');
  const unit = chart.unit || 'in';

  /* Columns are Size | Full Chest | Shoulder | Length for tops and
     Size | Waist | Half Hip | Length for bottoms - the same order the size
     guide snippet prints its headers in. "N/A" appears in real data (the
     raglan tee has no shoulder figure), so every cell is checked. */
  function num(cell) {
    if (cell == null) return null;
    const n = parseFloat(String(cell).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  function rowFor(rows, size) {
    const want = String(size).trim().toLowerCase();
    return rows.find(r => String(r[0]).trim().toLowerCase() === want) || null;
  }
  function fmt(n) {
    return (Math.round(n * 10) / 10) + unit;
  }

  function describe(row) {
    const parts = [];
    const primary = num(row[1]);
    const length = num(row[3]);
    if (primary !== null) parts.push(fmt(primary) + (isBottom ? ' waist' : ' chest'));
    if (length !== null) parts.push(fmt(length) + ' length');
    return parts;
  }

  /* The anchor only fires when the gap is big enough to actually mislead
     somebody. Thresholds are deliberately asymmetric: ordering 4in too small
     is unwearable and comes back, while 4in too roomy is often just the look
     people wanted, so the roomy side needs a wider gap before we say anything.
     Both numbers come from the return split - every L/XXL "too large" return
     was on a block 6in or more above the tee. */
  const TIGHTER_BY = 4;
  const ROOMIER_BY = 6;

  function anchorFor(size, primary) {
    const ref = window.RWDY_FIT_REFERENCE;
    if (!ref || !Array.isArray(ref.rows) || isBottom || primary === null) return '';
    const refRow = rowFor(ref.rows, size);
    if (!refRow) return '';
    const refChest = num(refRow[1]);
    if (refChest === null) return '';
    const diff = refChest - primary;
    if (diff >= TIGHTER_BY) {
      return 'Runs smaller than our oversized tee, which is ' + fmt(refChest) +
             ' at ' + size + '. Size up if you want the usual RWDY room.';
    }
    if (-diff >= ROOMIER_BY) {
      return 'Much roomier than our oversized tee, which is ' + fmt(refChest) +
             ' at ' + size + '. Size down for a closer fit.';
    }
    return '';
  }

  function show(size) {
    const row = rowFor(chart.rows, size);
    if (!row) { measureEl.hidden = true; if (anchorEl) anchorEl.hidden = true; return; }
    const parts = describe(row);
    if (!parts.length) { measureEl.hidden = true; if (anchorEl) anchorEl.hidden = true; return; }
    measureEl.textContent = size + ' measures ' + parts.join(', ') + '.';
    measureEl.hidden = false;
    if (!anchorEl) return;
    const msg = anchorFor(size, num(row[1]));
    anchorEl.textContent = msg;
    anchorEl.hidden = !msg;
  }

  /* Delegated, because the size buttons are re-rendered by syncSizeAvail when
     the colour changes. Position 1 is colour on two-option products, so the
     button's own label is matched against the chart instead of trusting
     position - a colour name will simply not match any row and is ignored. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.size-btn');
    if (!btn || btn.classList.contains('is-oos') || btn.classList.contains('rwdy-sib')) return;
    const value = (btn.dataset.value || btn.textContent || '').trim();
    if (!value) return;
    if (rowFor(chart.rows, value)) show(value);
  });
}

/* ============================================================
   STICKY ATC (Product Page) — enhanced with size + price sync
   ============================================================ */
function initStickyAtc() {
  const atcBar = document.querySelector('.sticky-atc');
  const trigger = document.querySelector('.product-actions');
  if (!atcBar || !trigger) return;

  const observer = new IntersectionObserver(([entry]) => {
    const visible = !entry.isIntersecting;
    atcBar.classList.toggle('is-visible', visible);
    atcBar.setAttribute('aria-hidden', !visible);
    // 17 Sep 2026 — flag it on <body> so the floating WhatsApp and back-to-top buttons can get
    // out of the way. The bubble sat at bottom 58+14px on the right, directly on top of the
    // sticky bar's own label: audit screenshots of the PDP read "ADD TO CA". CSS can't reach
    // the floats from here (they aren't siblings), so the state goes on the body.
    document.body.classList.toggle('has-sticky-atc', visible);
  }, { threshold: 0 });
  observer.observe(trigger);

  const stickyBtn = document.getElementById('sticky-atc-btn');
  const mainAtcBtn = document.querySelector('.product-actions .btn--solid');
  if (stickyBtn && mainAtcBtn) {
    stickyBtn.addEventListener('click', () => mainAtcBtn.click());
  }

  // Sync selected size + price into sticky bar
  const stickyPrice = document.getElementById('sticky-atc-price');
  const stickySizeWrap = document.getElementById('sticky-atc-size');
  const stickySizeVal = document.getElementById('sticky-atc-size-val');

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.size-btn');
    if (!btn || btn.classList.contains('is-oos')) return;
    if (stickySizeVal) stickySizeVal.textContent = btn.dataset.value;
    if (stickySizeWrap) stickySizeWrap.style.display = '';
  });

  // Also sync price when variant changes (listen to price el mutation)
  const mainPrice = document.querySelector('.product-price__current');
  if (mainPrice && stickyPrice) {
    new MutationObserver(() => {
      stickyPrice.textContent = mainPrice.textContent;
    }).observe(mainPrice, { childList: true, characterData: true, subtree: true });
  }
}

/* ============================================================
   FIND MY SIZE — shared predictor (product page + quick view use this)
   ============================================================ */
window.rwdyFindSize = function (w, h, pref, sizes, chart) {
  w = +w; h = +h;
  if (!w || !h || w < 20 || w > 300 || h < 100 || h > 250) {
    return { error: 'Please enter a valid weight and height.' };
  }
  sizes = (sizes || []).map(function (s) { return String(s).trim(); }).filter(Boolean);
  var numeric = sizes.filter(function (s) { return /^\d+$/.test(s); });

  /* ---- Per-style chart (window.RWDY_SIZE_CHART, from the product's size_chart
     metaobject). If the team has filled a "Fits kg" range per size, recommend the
     size whose range contains the customer's weight - this replaces the one-size-fits-all formula that caused wrong recommendations across different cuts. ---- */
  var fitName = (chart && chart.fit) ? String(chart.fit) : '';
  var fitNote = fitName === 'Oversized' ? 'cut oversized' : fitName === 'Relaxed' ? 'a relaxed fit'
    : fitName === 'Slim' ? 'a slim fit' : fitName === 'Regular' ? 'a regular fit' : '';
  function prefWord() {
    return pref === 'slim' ? 'sized down for a closer fit' : pref === 'oversized' ? 'sized up for extra room' : 'true to size';
  }
  if (chart && chart.rows && chart.rows.length) {
    var ranged = [];
    chart.rows.forEach(function (row) {
      if (!row || row.length < 2) return;
      var label = String(row[0]).trim(), lo = null, hi = null;
      row.slice(1).forEach(function (cell) {
        var m = String(cell).match(/^\s*(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*$/);
        if (m) { lo = +m[1]; hi = +m[2]; }
      });
      if (lo !== null) ranged.push({ label: label, lo: lo, hi: hi });
    });
    if (ranged.length) {
      var idx = -1;
      ranged.forEach(function (r, i) { if (idx < 0 && w >= r.lo && w <= r.hi) idx = i; });
      if (idx < 0) {                     // outside every range: snap to nearest edge
        if (w < ranged[0].lo) idx = 0;
        else if (w > ranged[ranged.length - 1].hi) idx = ranged.length - 1;
        else { var bd = 1e9; ranged.forEach(function (r, i) { var d = Math.min(Math.abs(w - r.lo), Math.abs(w - r.hi)); if (d < bd) { bd = d; idx = i; } }); }
      }
      // height nudge only for letter sizes, preference shift for all
      var isLetters = !/^\d+$/.test(ranged[0].label);
      if (isLetters) { if (h >= 190 && idx < ranged.length - 1) idx++; else if (h <= 158 && idx > 0) idx--; }
      if (pref === 'oversized' && idx < ranged.length - 1) idx++; else if (pref === 'slim' && idx > 0) idx--;
      var pick = ranged[idx].label;
      // only recommend a size this product actually sells
      var upS = sizes.map(function (x) { return x.toUpperCase(); });
      if (sizes.length && upS.indexOf(pick.toUpperCase()) === -1) {
        var bi = idx, bd2 = 1e9;
        ranged.forEach(function (r, i) { if (upS.indexOf(r.label.toUpperCase()) > -1 && Math.abs(i - idx) < bd2) { bd2 = Math.abs(i - idx); bi = i; } });
        pick = ranged[bi].label;
      }
      return { size: pick, note: prefWord() + (fitNote ? ' (' + chart.name + ', ' + fitNote + ')' : '') };
    }
  }

  // ---- Fallback: no chart ranges yet. Generic formula, but with honest advice text. ----
  var note = prefWord() + (fitNote ? ' for ' + fitNote : '');

  // BOTTOMS — numeric waist sizes (28, 30, 32 …). Predict waist from weight; snap to what's offered.
  if (numeric.length >= 2) {
    var waist;
    if (w <= 55) waist = 28; else if (w <= 63) waist = 30; else if (w <= 72) waist = 32;
    else if (w <= 81) waist = 34; else if (w <= 92) waist = 36; else waist = 38;
    if (pref === 'slim') waist -= 2; else if (pref === 'oversized') waist += 2;
    var nums = numeric.map(Number).sort(function (a, b) { return a - b; });
    var bestN = nums[0];
    nums.forEach(function (n) { if (Math.abs(n - waist) < Math.abs(bestN - waist)) bestN = n; });
    return { size: String(bestN), note: (pref === 'slim' ? 'a snugger waist' : pref === 'oversized' ? 'a relaxed waist' : 'a regular waist fit') };
  }

  // TOPS — letter sizes (XS … XXL)
  var order = ['XS', 'S', 'M', 'L', 'XL', 'XXL'], i;
  if (w <= 50) i = 0; else if (w <= 60) i = 1; else if (w <= 69) i = 2;
  else if (w <= 79) i = 3; else if (w <= 89) i = 4; else i = 5;
  if (h >= 190 && i < 5) i++; else if (h <= 158 && i > 0) i--;
  if (pref === 'oversized' && i < 5) i++; else if (pref === 'slim' && i > 0) i--;
  var want = order[i];
  // snap to the sizes this product actually offers (if provided)
  var up = sizes.map(function (x) { return x.toUpperCase(); });
  var avail = order.filter(function (s) { return up.indexOf(s) > -1; });
  if (avail.length && avail.indexOf(want) === -1) {
    var wi = order.indexOf(want), bestS = avail[0], bestD = 99;
    avail.forEach(function (s) { var d = Math.abs(order.indexOf(s) - wi); if (d < bestD) { bestD = d; bestS = s; } });
    want = bestS;
  }
  return { size: want, note: note };
};

/* ============================================================
   FIND MY SIZE — EXACT mode. The customer enters their own body chest (tops) or
   waist (bottoms); we pick the smallest size whose GARMENT measurement gives the
   right amount of room for that cut. Far more reliable than weight/height.
   ============================================================ */
window.rwdyFindSizeExact = function (measure, unit, pref, sizes, chart, forceBottoms) {
  var m = parseFloat(measure);
  if (!m || m <= 0) return { error: 'Please enter your measurement.' };
  if (unit === 'cm') m = m / 2.54;
  sizes = (sizes || []).map(function (x) { return String(x).trim(); }).filter(Boolean);
  var allNumeric = sizes.length && sizes.every(function (x) { return /^\d+$/.test(x); });
  var bottoms = (typeof forceBottoms === 'boolean') ? forceBottoms
    : (typeof window.RWDY_GARMENT_BOTTOM === 'boolean') ? window.RWDY_GARMENT_BOTTOM
    : (chart && chart.type ? String(chart.type).toLowerCase() === 'bottoms' : allNumeric);
  if (bottoms ? (m < 20 || m > 60) : (m < 24 || m > 70)) {
    return { error: 'That measurement looks off. Enter it in ' + (unit === 'cm' ? 'centimetres' : 'inches') + '.' };
  }
  var fit = chart && chart.fit ? String(chart.fit) : 'Regular';
  // minimum room (garment minus body, inches) that still feels like this cut
  // RWDY cuts generously: their XS shirt is 42" and XS oversized tee 44", i.e. ~5-6.5" of room is 'as designed'.
  var minEase = fit === 'Oversized' ? 6.5 : fit === 'Relaxed' ? 5.5 : fit === 'Slim' ? 3 : 5;
  if (pref === 'slim') minEase = Math.max(0.5, minEase - 2);
  else if (pref === 'oversized') minEase += 2;

  var rows = (chart && chart.rows) ? chart.rows.map(function (r) {
    return { label: String(r[0]).trim(), g: parseFloat(r[1]) };
  }).filter(function (r) { return r.label && !isNaN(r.g); }) : [];

  var pick, room;
  /* 23 Sep 2026: TOPS now read the size guide directly, so Find My Size and the guide
     always agree: the answer is the smallest size (in XS..XXL order) whose FULL CHEST in
     the guide is at least the customer's chest. If the chest is bigger than every size,
     the largest size is returned. Bottoms are unchanged (smallest waist >= their waist). */
  var ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  var byL = {};
  rows.forEach(function (r) { byL[r.label.toUpperCase()] = r; });
  var inOrder = ORDER.filter(function (s) { return byL[s]; }).map(function (s) { return byL[s]; });
  if (rows.length && !bottoms && inOrder.length >= 2) {
    var ix = -1;
    for (var k = 0; k < inOrder.length; k++) { if (inOrder[k].g >= m) { ix = k; break; } }
    if (ix < 0) ix = inOrder.length - 1;
    ix += (pref === 'oversized' ? 1 : pref === 'slim' ? -1 : 0);
    ix = Math.max(0, Math.min(inOrder.length - 1, ix));
    rows = inOrder;
    pick = inOrder[ix].label; room = inOrder[ix].g - m;
  } else if (rows.length) {
    rows.sort(function (a, b) { return a.g - b.g; });
    var need = bottoms ? m + (pref === 'oversized' ? 1 : pref === 'slim' ? -1 : 0) : m + minEase;
    var hit = null;
    for (var i = 0; i < rows.length; i++) { if (rows[i].g >= need) { hit = rows[i]; break; } }
    if (!hit) hit = rows[rows.length - 1];
    pick = hit.label; room = hit.g - m;
  } else {
    // no chart rows: standard body-measurement fallback
    if (bottoms) {
      var nums = sizes.map(Number).filter(function (n) { return n > 0; }).sort(function (a, b) { return a - b; });
      var want = m + (pref === 'oversized' ? 1 : pref === 'slim' ? -1 : 0);
      pick = String(nums.filter(function (n) { return n >= want; })[0] || nums[nums.length - 1] || Math.round(m));
    } else {
      var order = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
      var idx = m <= 34 ? 0 : m <= 36 ? 1 : m <= 38 ? 2 : m <= 40 ? 3 : m <= 42 ? 4 : 5;
      if (pref === 'oversized' && idx < 5) idx++; else if (pref === 'slim' && idx > 0) idx--;
      pick = order[idx];
    }
  }
  // only recommend a size this product sells (nearest sold one if not)
  var up = sizes.map(function (x) { return x.toUpperCase(); });
  if (sizes.length && up.indexOf(String(pick).toUpperCase()) === -1 && rows.length) {
    var pi = rows.findIndex(function (r) { return r.label === pick; }), best = null, bd = 1e9;
    rows.forEach(function (r, i) { if (up.indexOf(r.label.toUpperCase()) > -1 && Math.abs(i - pi) < bd) { bd = Math.abs(i - pi); best = r; } });
    if (best) { pick = best.label; room = best.g - m; }
  }
  var unitLbl = unit === 'cm' ? 'cm' : '"';
  var shown = unit === 'cm' ? Math.round(m * 2.54) : (Math.round(m * 2) / 2);
  var why = '';
  if (room !== undefined) {
    var roomShown = unit === 'cm' ? Math.round(room * 2.54) : (Math.round(room * 2) / 2);
    var gShown = unit === 'cm' ? Math.round((m + room) * 2.54 * 10) / 10 : Math.round((m + room) * 100) / 100;
    why = (bottoms ? 'Your waist ' : 'Your chest ') + shown + unitLbl + ' → ' + pick + '. In the size guide, ' + pick +
      ' has a ' + gShown + unitLbl + (bottoms ? ' waist' : ' full chest') + (room < 0 ? ' (our largest size)' : '') + '.';
  }
  return { size: pick, note: 'matched to this garment’s measurements', why: why };
};

/* Select a size button for the customer (used after a Find My Size recommendation so the
   right size is already chosen when they hit Add to cart). Skips sold-out sizes. Returns true if selected. */
window.rwdySelectSize = function (size) {
  if (!size) return false;
  var want = String(size).trim().toUpperCase();
  var btns = Array.prototype.slice.call(document.querySelectorAll('.size-btn[data-value]'));
  var btn = btns.filter(function (b) {
    var v = String(b.getAttribute('data-value') || '').trim().toUpperCase();
    return (v === want || v.split('-')[0] === want || v.split('/')[0].trim() === want) && !b.classList.contains('is-oos');
  })[0];
  if (!btn) return false;
  btn.click();
  return true;
};

/* Saved measurements: account metafield (window.RWDY_MEASUREMENTS, injected when logged in)
   with a localStorage cache so it also works instantly on-device. */
window.rwdyGetMeasurements = function () {
  if (window.RWDY_MEASUREMENTS && typeof window.RWDY_MEASUREMENTS === 'object') {
    try { localStorage.setItem('rwdy_measurements', JSON.stringify(window.RWDY_MEASUREMENTS)); } catch (e) {}
    return window.RWDY_MEASUREMENTS;
  }
  try { return JSON.parse(localStorage.getItem('rwdy_measurements') || 'null'); } catch (e) { return null; }
};
window.rwdySaveMeasurements = function (data) {
  window.RWDY_MEASUREMENTS = data;
  try { localStorage.setItem('rwdy_measurements', JSON.stringify(data)); } catch (e) {}
  // cross-device: persist to the customer account via the app proxy (built by the dev).
  if (window.RWDY_LOGGED_IN) {
    try {
      fetch('/apps/rwdy/measurements', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      }).catch(function () {});
    } catch (e) {}
  }
};

// Product page size finder: one number in (chest or waist) -> "Your size: M", selected.
function initFitMemory() {
  var meas = document.getElementById('fit-measure'), unit = document.getElementById('fit-unit');
  var findBtn = document.getElementById('fit-find'), res = document.getElementById('fit-result');
  var lbl = document.getElementById('fit-measure-label'), hint = document.getElementById('fit-measure-hint');
  if (!findBtn || !meas) return;
  var sizes = window.RWDY_FIT_SIZES || window.RWDY_PDP_SIZES || [];
  var chart = window.RWDY_SIZE_CHART || null;
  var allNumeric = sizes.length && sizes.every(function (x) { return /^\d+$/.test(String(x).trim()); });
  // bottomwear is decided by the PRODUCT TYPE (set in Liquid); chart/size labels are only fallbacks
  var bottoms = (typeof window.RWDY_GARMENT_BOTTOM === 'boolean') ? window.RWDY_GARMENT_BOTTOM
    : (chart && chart.type ? String(chart.type).toLowerCase() === 'bottoms' : allNumeric);

  if (lbl) lbl.textContent = bottoms ? 'waist' : 'chest / bust';
  meas.placeholder = bottoms ? '32' : '40';
  if (hint) hint.textContent = bottoms
    ? 'Measure around your waist where your jeans sit. Unisex sizing: same for everyone.'
    : 'Measure around the fullest part of your chest or bust. Unisex sizing: same for everyone.';

  function answer() {
    var r = window.rwdyFindSizeExact(meas.value, unit ? unit.value : 'in', 'regular', sizes, chart, bottoms);
    if (r.error) { res.textContent = r.error; res.style.color = '#c0392b'; return; }
    var picked = window.rwdySelectSize && window.rwdySelectSize(r.size);
    res.textContent = 'Your size: ' + r.size + (picked ? '  ✓ selected' : '');
    res.style.color = '#3f6b3f';
    // 23 Sep: say which size-guide row this came from, and highlight it in the guide
    var whyEl = document.getElementById('fit-why');
    if (!whyEl && res.parentNode) {
      whyEl = document.createElement('div'); whyEl.id = 'fit-why';
      whyEl.style.cssText = 'flex-basis:100%;font-size:12.5px;color:#3f3f3c;margin-top:-4px';
      res.parentNode.insertBefore(whyEl, res.nextSibling);
    }
    if (whyEl) whyEl.textContent = r.why || '';
    document.querySelectorAll('.sg-widget .sg-table tbody tr').forEach(function (tr) {
      var c0 = tr.firstElementChild;
      tr.classList.toggle('sg-rec', !!c0 && c0.textContent.trim().toUpperCase() === String(r.size).toUpperCase());
    });
    // remember it (account + device) so it pre-fills and auto-picks next time
    var data = Object.assign({}, window.rwdyGetMeasurements() || {});
    var v = parseFloat(meas.value); if (unit && unit.value === 'cm') v = v / 2.54;
    v = Math.round(v * 2) / 2; if (bottoms) data.waist = v; else data.chest = v;
    window.rwdySaveMeasurements(data);
  }
  findBtn.addEventListener('click', answer);
  meas.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); answer(); } });

  // returning customer: pre-fill and answer immediately
  var saved = window.rwdyGetMeasurements();
  var savedM = saved && (bottoms ? saved.waist : saved.chest);
  if (savedM) { meas.value = savedM; if (unit) unit.value = 'in'; answer(); }
}

/* ============================================================
   QUICK VIEW MODAL
   ============================================================ */
function initQuickView() {
  let quickViewColour = null;   // set by the clicked card, consumed by renderQuickView
  const modal = document.getElementById('quick-view-modal');
  const closeBtn = document.getElementById('quick-view-close');
  const body = document.getElementById('quick-view-body');
  if (!modal) return;

  function closeModal() {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    unlockBody();
    if (body) body.innerHTML = '<div class="quick-view-modal__loading">Loading…</div>';
  }

  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !modal.classList.contains('is-open')) return;
    // 28 Sep 2026: the size guide can now be open ON TOP of Quick View. Escape has to close
    // the top layer first, or it shuts Quick View out from underneath and leaves the size
    // guide floating over the collection page. initSizeGuide() owns Escape while it is open.
    const sg = document.getElementById('size-guide-modal');
    if (sg && sg.classList.contains('is-open')) return;
    closeModal();
  });

  /* Size Guide inside Quick View (28 Sep 2026).
     Quick View is built from /products/{handle}.js, which returns the product JSON and carries
     no metafields — so the product's size_chart is simply not in memory here. Fetch the product
     page once on click and lift #pdp-size-guide-content out of it: that is the very same
     per-product chart the PDP shows, so the two can never disagree.

     Deliberately NO generic-table fallback. Showing the oversized-tee chart on a kurta is the
     exact problem the per-product charts were built to solve, so if the chart can't be had we
     send the customer to the product page instead of showing them the wrong numbers. */
  const qvChartCache = {};
  document.addEventListener('click', async (e) => {
    const link = e.target.closest('[data-qv-size-guide]');
    if (!link) return;
    e.preventDefault();
    const handle = link.getAttribute('data-qv-size-guide');
    const sgModal = document.getElementById('size-guide-modal');
    const sgInner = sgModal && sgModal.querySelector('.size-guide-modal__inner');
    if (!sgModal || !sgInner) { window.location = '/products/' + handle + '#size-guide'; return; }

    let chartHtml = qvChartCache[handle];
    if (chartHtml === undefined) {
      const label = link.textContent;
      link.textContent = 'Size Guide…';
      try {
        const res = await fetch('/products/' + handle);
        if (!res.ok) throw new Error('product page ' + res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const src = doc.getElementById('pdp-size-guide-content');
        chartHtml = src ? src.innerHTML : null;
      } catch (err) {
        chartHtml = null;
      }
      qvChartCache[handle] = chartHtml;
      link.textContent = label;
    }
    if (!chartHtml) { window.location = '/products/' + handle + '#size-guide'; return; }

    const sgClose = document.getElementById('size-guide-close');
    Array.from(sgInner.children).forEach(el => { if (el !== sgClose) el.remove(); });
    const wrap = document.createElement('div');
    wrap.innerHTML = chartHtml;
    wrap.removeAttribute('hidden');
    sgInner.appendChild(wrap);
    sgModal.classList.add('is-open');
  });

  // Delegate clicks on quick-view buttons
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-view]');
    if (!btn) return;
    e.preventDefault();
    const handle = btn.dataset.quickView;
    if (!handle) return;
    // 5 Oct 2026: colour cards. The grid renders one tile per colour, so Quick View has to open
    // on the colour that was clicked - otherwise tapping "Choose Size" on the Navy tile shows
    // the default colour's photo and swatch, which reads as a bug.
    quickViewColour = btn.dataset.quickViewColor || null;

    // Open modal with loading state
    if (body) body.innerHTML = '<div class="quick-view-modal__loading">Loading…</div>';
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    lockBody();

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error('Product not found');
      const p = await res.json();
      renderQuickView(p);
      if (btn.dataset.pairOffer && body) {
        const note = document.createElement('div');
        note.className = 'qv-pair-note';
        note.innerHTML = 'Pair offer: <b>30% off this piece</b> as your second item — applied at checkout when you pay online (not on COD).';
        body.prepend(note);
      }
    } catch (err) {
      if (body) body.innerHTML = '<div style="padding:40px;text-align:center;color:var(--c-muted)">Could not load product.</div>';
    }
  });

  function renderQuickView(p) {
    const imgs = p.images.slice(0, 5).map(function (i) { return typeof i === 'string' ? i : ((i && i.src) || ''); });
    const optionNames = (p.options || []).map(function (o) { return typeof o === 'string' ? o : (o && o.name) || ''; });
    const variants = p.variants || [];
    const valOf = (v, i) => v['option' + (i + 1)];
    // which option rows to show (skip Shopify's default "Title" option on single-variant products)
    const showOpts = optionNames.map((n, i) => i).filter(i => optionNames[i] !== 'Title');
    const optionValues = optionNames.map((n, i) => {
      const seen = [];
      variants.forEach(v => { const val = valOf(v, i); if (val != null && seen.indexOf(val) === -1) seen.push(val); });
      return seen;
    });
    // Size-aware Find My Size: only for real sizes (letters/waist numbers), not universal/one-size
    const sizeOptIdx = optionNames.findIndex(n => /size/i.test(n));
    const sizeValues = sizeOptIdx > -1 ? optionValues[sizeOptIdx] : [];
    const showFit = sizeValues.length >= 2 && !sizeValues.some(s => /universal|one\s*size|free\s*size/i.test(String(s)));
    /* 10 Oct 2026: the size predictor ("Tell me my size") is switched off everywhere -
       customers were confusing it with the size guide. Deliberately a SEPARATE flag from
       showFit, because showFit also gates the Size Guide link above, which stays. Set this
       back to showFit to bring the predictor back. */
    const showSizeFinder = false;

    const initVariant = variants.find(v => v.available) || variants[0] || {};
    // 17 Sep 2026 — the XS bug, third location. Quick View seeded every option from the first
    // available variant, so the size row opened with XS already highlighted and its Add to Cart
    // added XS. Colour still defaults (the gallery needs one); size starts genuinely unchosen.
    const selected = optionNames.map((n, i) => {
      if (/size/i.test(n)) return undefined;                 // size stays unchosen - 17 Sep XS fix
      if (quickViewColour && /colou?r/i.test(n)) {
        // match case-insensitively: card markup and product JSON disagree on casing in places
        const hit = optionValues[i].find(v => String(v).toLowerCase() === quickViewColour.toLowerCase());
        if (hit) return hit;
      }
      return valOf(initVariant, i);
    });
    const sizeIdx = optionNames.findIndex(n => /size/i.test(n));
    const sizePicked = () => sizeIdx === -1 || selected[sizeIdx] != null;

    const findVariant = () => variants.find(v => optionNames.every((n, i) => valOf(v, i) === selected[i]));
    // BUGFIX (18 Sep 2026): an option with nothing chosen yet must mean "any", not
    // "must equal undefined". Since 17 Sep the size deliberately starts unchosen (the
    // "Select a size" gate), so `valOf(v, sizeIdx) === selected[sizeIdx]` compared every
    // variant against undefined, matched nothing, and struck through EVERY COLOUR until a
    // size was picked — telling customers the whole product was sold out.
    // Skipping unchosen options gives the intended behaviour: a colour is available if any
    // size in it is available, and once a size is chosen the colours narrow to that size.
    const valueAvailable = (i, val) => variants.some(v =>
      v.available && valOf(v, i) === val &&
      optionNames.every((n, j) => j === i || selected[j] == null || valOf(v, j) === selected[j]));
    const priceHtml = (v) => !v ? '' : (v.compare_at_price > v.price
      ? `<span class="product-price__current product-price__sale-price">${formatMoney(v.price)}</span> <span class="product-price__compare">${formatMoney(v.compare_at_price)}</span>`
      : `<span class="product-price__current">${formatMoney(v.price)}</span>`);
    const esc = (s) => String(s).replace(/"/g, '&quot;');

    body.innerHTML = `
      <div class="qv-gallery">
        <img class="qv-gallery__main" id="qv-main-img"
          src="${imgs[0] ? imgs[0].replace(/\.(jpg|png|webp)(\?.*)?$/, '_720x960.$1') : ''}"
          alt="${p.title}">
        ${imgs.length > 1 ? `<div class="qv-gallery__thumbs">${imgs.slice(1, 5).map(img => `
          <img class="qv-gallery__thumb" src="${img.replace(/\.(jpg|png|webp)(\?.*)?$/, '_160x200.$1')}"
            data-full="${img.replace(/\.(jpg|png|webp)(\?.*)?$/, '_720x960.$1')}"
            alt="${p.title}">
        `).join('')}</div>` : ''}
      </div>
      <div class="qv-info">
        <div class="qv-info__title">${p.title}</div>
        <div class="qv-info__price" id="qv-price">${priceHtml(findVariant())}</div>
        ${showOpts.map(i => `
        <div class="qv-info__label">${optionNames[i]}: <strong class="qv-opt-selected" data-opt="${i}"></strong>${i === sizeOptIdx && showFit ? ` <a href="${p.url}#size-guide" class="qv-size-guide" data-qv-size-guide="${esc(p.handle)}">Size Guide</a>` : ''}</div>
        <div class="qv-sizes qv-optrow" data-opt="${i}">
          ${optionValues[i].map(val => `
            <button class="size-btn" data-opt="${i}" data-value="${esc(val)}">${val}</button>
          `).join('')}
        </div>`).join('')}
        <div class="qv-info__actions">
          <button class="btn btn--solid btn--full" id="qv-atc-btn">Add to Cart</button>
        </div>
        <a href="${p.url}" class="qv-info__full-link">View full details →</a>
        ${showSizeFinder ? `
        <div class="qv-fit" style="margin-top:10px;border:1px solid #f0f0ed;padding:10px 12px;display:flex;flex-wrap:wrap;align-items:center;gap:8px">
          <label for="qv-fit-measure" style="font-size:12px;font-weight:700;margin:0">Your <span id="qv-fit-label">chest</span></label>
          <input type="number" step="0.5" id="qv-fit-measure" placeholder="40" class="form-input" style="width:78px;margin:0" aria-label="Your measurement">
          <select id="qv-fit-unit" class="form-select" style="width:68px;margin:0" aria-label="Unit"><option value="in" selected>in</option><option value="cm">cm</option></select>
          <button type="button" id="qv-fit-btn" class="btn btn--solid" style="padding:8px 14px;font-size:11px">Tell me my size</button>
          <div id="qv-fit-result" style="flex-basis:100%;font-size:13px;font-weight:800;color:#3f6b3f" aria-live="polite"></div>
        </div>` : ''}
      </div>
    `;

    function refresh() {
      const cur = findVariant();
      // With no size chosen, findVariant() matches nothing, which would blank the price and
      // the colour image. Use a representative variant (the chosen colour's first available)
      // for DISPLAY only — the Add to Cart gate below still keys off sizePicked(), so this
      // cannot reintroduce an accidental add. (17 Sep 2026)
      const shown = cur || variants.find(v => v.available && optionNames.every(
        (n, i) => selected[i] == null || valOf(v, i) === selected[i])) || initVariant;
      const priceEl = body.querySelector('#qv-price');
      if (priceEl) priceEl.innerHTML = priceHtml(shown);
      // show the selected variant's (colour's) own image, if one is assigned in Shopify
      const mainImg = body.querySelector('.qv-gallery__main');
      const fi = shown && shown.featured_image;
      const fiSrc = fi && (typeof fi === 'string' ? fi : fi.src);
      if (mainImg && fiSrc) {
        mainImg.src = fiSrc.replace(/\.(jpg|png|webp)(\?.*)?$/, '_720x960.$1');
      }
      showOpts.forEach(i => {
        body.querySelectorAll('.qv-optrow[data-opt="' + i + '"] .size-btn').forEach(btn => {
          const val = btn.dataset.value;
          btn.classList.toggle('is-selected', val === String(selected[i]));
          const ok = valueAvailable(i, val);
          btn.classList.toggle('is-oos', !ok);
          if (ok) btn.removeAttribute('aria-disabled'); else btn.setAttribute('aria-disabled', 'true');
        });
        const lbl = body.querySelector('.qv-opt-selected[data-opt="' + i + '"]');
        if (lbl) lbl.textContent = selected[i] != null ? selected[i] : '';
      });
      const atcBtn = body.querySelector('#qv-atc-btn');
      if (atcBtn) {
        if (!sizePicked()) {
          // No size chosen yet: no variant id on the button, so there is nothing to add even
          // if something else clicks it. (17 Sep 2026)
          atcBtn.dataset.variantId = '';
          atcBtn.textContent = 'Select a size';
          atcBtn.disabled = true;
        } else {
          const avail = cur && cur.available;
          atcBtn.dataset.variantId = cur ? cur.id : '';
          atcBtn.textContent = avail ? 'Add to Cart' : (cur ? 'Out of Stock' : 'Unavailable');
          atcBtn.disabled = !avail;
        }
      }
    }

    // Thumb switcher
    body.querySelectorAll('.qv-gallery__thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        const main = body.querySelector('.qv-gallery__main');
        if (main) main.src = thumb.dataset.full;
      });
    });

    // Option selection
    body.querySelectorAll('.qv-optrow .size-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const i = parseInt(btn.dataset.opt, 10);
        selected[i] = btn.dataset.value;
        refresh();
      });
    });

    // ATC
    const atcBtn = body.querySelector('#qv-atc-btn');
    if (atcBtn) {
      atcBtn.addEventListener('click', async () => {
        const vid = parseInt(atcBtn.dataset.variantId, 10);
        if (!vid) return;
        atcBtn.textContent = 'Adding…';
        atcBtn.disabled = true;
        await addToCart(vid, 1, true);
        closeModal();
      });
    }

    // Size finder (quick view). No size chart is available here (the .js endpoint has no
    // metafields), so it uses the standard body-measurement mapping; the product page is exact.
    if (showSizeFinder) {
      const qm = body.querySelector('#qv-fit-measure'), qu = body.querySelector('#qv-fit-unit');
      const qbtn = body.querySelector('#qv-fit-btn'), qres = body.querySelector('#qv-fit-result'), qlbl = body.querySelector('#qv-fit-label');
      const ptype = String(p.type || p.product_type || '').toLowerCase();
      const numeric = /jean|trouser|pant|short|cargo|bottom|jogger|denim/.test(ptype)
        || (sizeValues.length && sizeValues.every(v => /^\d+$/.test(String(v).trim())));
      if (qlbl) qlbl.textContent = numeric ? 'waist' : 'chest / bust';
      if (qm) qm.placeholder = numeric ? '32' : '40';
      // 23 Sep: quick view reads the same size chart as the product page (from the product
      // section's size guide table), so both give the same answer. Falls back to no chart.
      let qvChart;
      const loadQvChart = async () => {
        if (qvChart !== undefined) return qvChart;
        qvChart = null;
        try {
          const r = await fetch('/products/' + encodeURIComponent(p.handle) + '?section_id=main-product');
          if (r.ok) {
            const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
            const rows = Array.from(doc.querySelectorAll('#pdp-size-guide-content .sg-table tbody tr')).map(tr =>
              Array.from(tr.children).map(td => (td.getAttribute('data-in') || td.textContent).trim()));
            if (rows.length) qvChart = { rows: rows };
          }
        } catch (e) {}
        return qvChart;
      };
      const qanswer = async () => {
        const chartQ = await loadQvChart();
        const r = window.rwdyFindSizeExact(qm.value, qu ? qu.value : 'in', 'regular', sizeValues, chartQ, !!numeric);
        if (!qres) return;
        if (r.error) { qres.textContent = r.error; qres.style.color = '#c0392b'; return; }
        // select it in the modal's own size row
        let picked = false;
        body.querySelectorAll('.qv-optrow .size-btn').forEach(b => {
          if (!picked && String(b.dataset.value).toUpperCase() === String(r.size).toUpperCase() && !b.classList.contains('is-oos')) { b.click(); picked = true; }
        });
        qres.textContent = 'Your size: ' + r.size + (picked ? '  ✓ selected' : '');
        qres.style.color = '#3f6b3f';
        const data = Object.assign({}, window.rwdyGetMeasurements() || {});
        let v = parseFloat(qm.value); if (qu && qu.value === 'cm') v = v / 2.54; v = Math.round(v * 2) / 2;
        if (numeric) data.waist = v; else data.chest = v;
        window.rwdySaveMeasurements(data);
      };
      if (qbtn) qbtn.addEventListener('click', qanswer);
      if (qm) qm.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); qanswer(); } });
      const saved = window.rwdyGetMeasurements();
      const sm = saved && (numeric ? saved.waist : saved.chest);
      if (sm && qm) { qm.value = sm; qanswer(); }
    }

    refresh();
  }
}

/* ============================================================
   RECENTLY VIEWED — render from localStorage
   ============================================================ */
function initRecentlyViewed() {
  const grid = document.getElementById('recently-viewed-grid');
  if (!grid) return;

  const KEY = 'rwdy_rv';
  const currentPath = window.location.pathname;
  let items;
  try { items = JSON.parse(localStorage.getItem(KEY)) || []; } catch { items = []; }

  // Filter out the current page product
  items = items.filter(item => !currentPath.includes(item.url));

  if (items.length === 0) {
    const section = grid.closest('.recently-viewed');
    if (section) section.style.display = 'none';
    return;
  }

  grid.innerHTML = items.slice(0, 4).map(item => `
    <article class="product-card">
      <a href="${item.url}" class="product-card__media">
        <img class="product-card__img"
          src="${item.img || ''}"
          alt="${item.title}"
          loading="lazy">
      </a>
      <div class="product-card__info">
        <a href="${item.url}" class="product-card__name">${item.title}</a>
        <div class="product-card__price">${formatMoney(item.price)}</div>
      </div>
    </article>
  `).join('');
}

/* ============================================================
   CART UPSELL — show 1 recommended product in cart drawer
   ============================================================ */
/* ------------------------------------------------------------
   "You might also like" — cart drawer AND cart page.

   Every upsell block on the page is a `.cart-upsell` element containing a
   `[data-upsell-items]` grid. Blocks declare their own card count via
   `data-upsell-count`, so the narrow drawer can show 3 while the wide cart
   page shows 4, from a single shared fetch.
   ------------------------------------------------------------ */
const UPSELL_COUNT = 3;   // default when a block doesn't specify

function escapeUpsellHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Candidates relevant to the cart, excluding anything already in it.
async function getUpsellCandidates() {
  const cart = await fetch('/cart.js').then(r => r.json()).catch(() => ({ items: [] }));
  const cartItems = cart.items || [];
  const inCartIds = cartItems.map(i => i.product_id);

  // 1) Shopify's recommendation engine, seeded by a cart item.
  //    Prefer "complementary" (merchandiser-curated), fall back to "related".
  const seed = cartItems.length
    ? cartItems[Math.floor(Math.random() * cartItems.length)].product_id
    : null;

  if (seed) {
    for (const intent of ['complementary', 'related']) {
      try {
        const rec = await fetch(
          `/recommendations/products.json?product_id=${seed}&limit=12&intent=${intent}`
        ).then(r => r.json());
        const list = (rec.products || []).filter(
          p => p.available && inCartIds.indexOf(p.id) === -1
        );
        if (list.length) return list;
      } catch (e) { /* try next intent */ }
    }
  }

  // 2) Fallback: catalogue pool, still excluding what's already in the cart.
  //    NOTE: this endpoint ignores sort_by and returns products alphabetically,
  //    which is why the old code always showed the same item — we shuffle below.
  try {
    const data = await fetch('/collections/all/products.json?limit=50').then(r => r.json());
    return (data.products || []).filter(
      p => p.available && inCartIds.indexOf(p.id) === -1
    );
  } catch (e) {
    return [];
  }
}

function renderUpsellBlock(wrap, products) {
  // NOTE: layout/theme.liquid is NOT in deploy_updates.py's PUSH_FILES — that
  // file is patched in place on the live theme, so the drawer's markup will not
  // pick up new data-* attributes. Fall back to the existing .cart-upsell__item
  // class (and the default count) so the drawer keeps working either way.
  const grid = wrap.querySelector('[data-upsell-items]')
    || wrap.querySelector('.cart-upsell__item');
  if (!grid) return;

  let count = parseInt(wrap.dataset.upsellCount, 10) || UPSELL_COUNT;
  // Optional smaller count on phones, so the block doesn't push the checkout
  // button far down once a two-column layout collapses to one.
  const mobileCount = parseInt(wrap.dataset.upsellCountMobile, 10);
  if (mobileCount && window.matchMedia('(max-width: 749px)').matches) {
    count = mobileCount;
  }

  // Shuffle so the strip rotates between renders.
  const pool = products.slice();
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = pool[i]; pool[i] = pool[j]; pool[j] = t;
  }

  const cards = [];
  for (const pick of pool) {
    if (cards.length >= count) break;

    const variant = (pick.variants || []).find(v => v.available) || (pick.variants || [])[0];
    if (!variant) continue;

    // The two endpoints return DIFFERENT image shapes:
    //   /collections/*/products.json   → images: [{ src: "https://..." }]  (objects)
    //   /recommendations/products.json → images: ["//cdn.shopify..."]      (plain strings)
    // Reading .src blindly throws on the recommendations shape, which the
    // outer catch would swallow — leaving the upsell silently blank.
    const rawImg = pick.images && pick.images[0];
    const imgUrl = typeof rawImg === 'string' ? rawImg : (rawImg && rawImg.src) || '';
    const img = imgUrl ? imgUrl.replace(/\.(jpg|jpeg|png|webp)(\?.*)?$/i, '_400x500.$1') : '';
    const title = escapeUpsellHtml(pick.title);

    cards.push(`
      <div class="cart-upsell__card">
        <a class="cart-upsell__card-link" href="/products/${pick.handle}" aria-label="${title}">
          <img class="cart-upsell__img" src="${img}" alt="${title}" loading="lazy">
          <div class="cart-upsell__name">${title}</div>
        </a>
        <div class="cart-upsell__price">${formatMoney(variant.price)}</div>
        <button class="btn btn--outline cart-upsell__add"
          data-variant-id="${variant.id}"
          aria-label="Add ${title} to cart"
        >+ Add</button>
      </div>
    `);
  }

  if (!cards.length) { wrap.style.display = 'none'; return; }

  grid.innerHTML = cards.join('');
  const isMulti = cards.length > 1;
  grid.classList.toggle('cart-upsell__item--multi', isMulti);
  // Keep the column count in step with however many cards actually rendered
  // (fewer than `count` when the catalogue is thin or mostly in-cart).
  grid.style.setProperty('--upsell-cols', cards.length);
  wrap.style.display = 'block';

  // Delegated: one handler covers every card, and survives re-renders.
  if (!grid.dataset.upsellBound) {
    grid.dataset.upsellBound = '1';
    grid.addEventListener('click', async (e) => {
      const btn = e.target.closest('.cart-upsell__add');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      const original = btn.textContent;
      btn.textContent = '…';
      btn.disabled = true;
      try {
        // Third arg is openDrawer — always false here. In the drawer it is
        // already open (re-dispatching would re-render the strip mid-click),
        // and on the cart page we reload instead.
        await addToCart(parseInt(btn.dataset.variantId), 1, false);
        btn.textContent = '✓ Added';
        // On the cart page the line items and totals are server-rendered, so a
        // silent AJAX add would leave the page showing stale contents.
        if (wrap.dataset.upsellReload === 'true') {
          setTimeout(() => window.location.reload(), 450);
        }
      } catch (err) {
        btn.textContent = original;
        btn.disabled = false;
      }
    });
  }
}

async function initCartUpsell() {
  const wraps = [...document.querySelectorAll('.cart-upsell')];
  if (!wraps.length) return;
  try {
    const products = await getUpsellCandidates();
    if (!products.length) { wraps.forEach(w => { w.style.display = 'none'; }); return; }
    wraps.forEach(w => renderUpsellBlock(w, products));
  } catch (err) {
    console.error('Cart upsell fetch failed', err);
  }
}

/* ============================================================
   ACCORDION
   ============================================================ */
function initAccordions() {
  // FAQ items carry both classes and get their own handler in initFaqAccordions —
  // binding both made every click toggle twice (open+close = dead accordion).
  document.querySelectorAll('.accordion-item__btn:not(.faq-item__btn)').forEach(btn => {
    btn.addEventListener('click', () => {
      const item = btn.closest('.accordion-item');
      if (!item) return;
      const wasOpen = item.classList.contains('is-open');
      // Close siblings
      const parent = item.parentElement;
      parent.querySelectorAll('.accordion-item').forEach(i => i.classList.remove('is-open'));
      if (!wasOpen) item.classList.add('is-open');
    });
  });
}

/* ============================================================
   SIZE GUIDE MODAL
   ============================================================ */
function initSizeGuide() {
  const modal = qs('size-guide-modal') || $('#size-guide-modal');
  const closeBtn = qs('size-guide-close') || $('#size-guide-close');
  if (!modal) return;

  // Per-product chart: the PDP renders #pdp-size-guide-content (from the product's
  // size_chart metaobject). On first open, replace the generic global table with it,
  // so jeans/trousers/kurtas stop showing the tee chart. Close button is preserved.
  // 28 Sep 2026: this used to run once per page load, guarded by a `swapped` flag. Quick View can
  // now write a DIFFERENT product's chart into this same modal, so a one-time swap would leave
  // whichever product was opened first on screen for the rest of the visit. Re-clone every time:
  // it is a table clone from a node already in the DOM, so the cost is nil.
  function applyProductChart() {
    const src = document.getElementById('pdp-size-guide-content');
    const inner = modal.querySelector('.size-guide-modal__inner');
    if (!src || !inner) return;
    Array.from(inner.children).forEach(el => { if (el !== closeBtn) el.remove(); });
    const clone = src.cloneNode(true);
    clone.removeAttribute('hidden'); clone.removeAttribute('id');
    inner.appendChild(clone);
  }

  document.querySelectorAll('[data-open-size-guide]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      applyProductChart();
      modal.classList.add('is-open');
    });
  });
  if (closeBtn) closeBtn.addEventListener('click', () => modal.classList.remove('is-open'));
  modal.addEventListener('click', (e) => { if (e.target === modal) modal.classList.remove('is-open'); });
  // 28 Sep 2026: Escape closes the size guide. It had none of its own, so while it was open
  // over Quick View, Escape fell through and closed Quick View instead. Quick View's handler
  // now stands down whenever this modal is open, so the two cannot both act on one keypress.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('is-open')) modal.classList.remove('is-open');
  });

  /* IN / CM toggle on the size chart.
     Moved here from a <script> tag inside snippets/rwdy-size-guide.liquid on 28 Sep 2026.
     That tag sat next to #pdp-size-guide-content rather than inside it, so it travelled
     nowhere: theme.js clones only the div, and the Quick View chart arrives as innerHTML
     over fetch, which never executes scripts at all. The toggle was dead outside the PDP.
     Delegated from document so it works on every copy of the chart, cloned or fetched. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('.sg-widget .sg-unit-btn');
    if (!btn) return;
    const w = btn.closest('.sg-widget');
    const unit = btn.getAttribute('data-unit');
    w.querySelectorAll('.sg-unit-btn').forEach(b => b.classList.toggle('is-active', b === btn));
    w.querySelectorAll('td[data-in]').forEach(td => {
      const raw = td.getAttribute('data-in');
      const v = parseFloat(raw);
      // non-numeric cells (e.g. "N/A" on the tank chart) are printed back untouched
      if (isNaN(v) || !/^\s*[\d.]+\s*$/.test(raw)) { td.textContent = raw; return; }
      td.textContent = unit === 'cm' ? String(Math.round(v * 2.54 * 10) / 10) : raw;
    });
  });
}

/* ============================================================
   PINCODE CHECKER
   ============================================================ */
function initPincodeChecker() {
  document.querySelectorAll('.pincode-checker').forEach(block => {
    const input = block.querySelector('.pincode-checker__input');
    const btn = block.querySelector('.pincode-checker__btn');
    const result = block.querySelector('.pincode-checker__result');
    if (!btn || !input) return;
    btn.addEventListener('click', () => {
      const pin = input.value.trim();
      if (pin.length !== 6 || isNaN(pin)) {
        if (result) { result.textContent = 'Please enter a valid 6-digit pincode.'; result.style.color = '#b3261e'; }
        return;
      }
      // 17 Sep 2026 — this used to be `// Simulate check` and answered
      // "Delivery available. Arrives in 5-7 days." for ANY six digits, including 000000.
      // It was inventing both serviceability and a date, and its "5-7 days" also contradicted
      // the 3-5 business days promised on the PDP, the FAQ and the shipping policy.
      // Until it is wired to real serviceability data (GoKwik Checkout → Settings → PIN Codes
      // Settings holds the list, but it is switched off and no list is uploaded), it must not
      // claim anything it cannot know. It now states only what is actually true.
      if (result) {
        result.style.color = '#3f3f3a';
        result.innerHTML = 'We ship across India. Orders leave us in 24–48 hours and usually ' +
          'arrive in <b>3–5 business days</b>. Serviceability for your pincode and any COD ' +
          'limit are confirmed at checkout.';
      }
    });
  });
}

/* ============================================================
   FAQ ACCORDIONS
   ============================================================ */
function initFaqAccordions() {
  document.querySelectorAll('.faq-item').forEach(item => {
    const btn = item.querySelector('.faq-item__btn');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const wasOpen = item.classList.contains('is-open');
      document.querySelectorAll('.faq-item').forEach(i => i.classList.remove('is-open'));
      if (!wasOpen) item.classList.add('is-open');
    });
  });
}

/* ============================================================
   CONTACT FORM
   ============================================================ */
function initContactForm() {
  const form = document.querySelector('.contact-form form');
  if (!form) return;
  form.addEventListener('submit', (e) => {
    const btn = form.querySelector('button[type="submit"]');
    if (btn) btn.textContent = '✓ Message Sent';
  });
}

/* ============================================================
   COLLECTION FILTERS
   ============================================================ */
function initCollectionFilters() {
  const toggleBtn = document.querySelector('.collection-toolbar__filter-btn');
  const layout = document.querySelector('.collection-layout');
  if (!toggleBtn || !layout) return;

  let filtersOpen = false;

  toggleBtn.addEventListener('click', () => {
    filtersOpen = !filtersOpen;
    layout.classList.toggle('has-filters', filtersOpen);
    const sidebar = layout.querySelector('.collection-filters');
    if (sidebar) sidebar.style.display = filtersOpen ? 'flex' : 'none';
    toggleBtn.textContent = filtersOpen ? 'Filter ✕' : 'Filter ▸';
  });
}

/* ============================================================
   GALLERY THUMBNAIL SWITCHER
   ============================================================ */
function initProductGallery() {
  const thumbs = $$('.product-gallery__thumb');
  const mainImg = document.querySelector('.product-gallery__main-img');
  if (!thumbs.length || !mainImg) return;

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
      const src = thumb.dataset.src || thumb.src;
      mainImg.src = src;
    });
  });
}

/* ============================================================
   WISHLIST PAGE
   ============================================================ */
function initWishlistPage() {
  const page = document.querySelector('.wishlist-page');
  if (!page) return;

  const grid = page.querySelector('.product-grid');
  const emptyState = page.querySelector('.wishlist-empty');
  const countEl = page.querySelector('.wishlist-page__sub');

  const ids = getWishlist();

  if (countEl) {
    countEl.textContent = ids.length === 0 ? '' : `${ids.length} saved item${ids.length > 1 ? 's' : ''}`;
  }

  if (ids.length === 0 && emptyState) {
    emptyState.style.display = 'block';
    if (grid) grid.style.display = 'none';
  }
}

/* ============================================================
   INIT ALL
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initCartDrawer();
  initSearch();
  initWishlist();
  initMobileNav();
  initBackToTop();
  initNewsletter();
  initShareBtn();
  initFitCheck();
  initOthersBought();
  initColorSwatches();
  initSpotted();
  initStickyAtc();
  initFitPanel();
  initAccordions();
  initSizeGuide();
  initPincodeChecker();
  initFaqAccordions();
  initContactForm();
  initCollectionFilters();
  initProductGallery();
  initWishlistPage();
  initNotifyMe();
  initStockIndicator();
  initQuickView();
  initFitMemory();
  initRecentlyViewed();
  initCarousel('.rwdy-frames');
  // Countdown — target date wired from section setting via data attribute
  const cdEl = document.querySelector('[data-countdown-target]');
  if (cdEl) initCountdown(cdEl.dataset.countdownTarget);
  // 18 Sep 2026 — do NOT load the cart upsell on page load.
  // initCartUpsell() ends up calling getUpsellCandidates(), which fetches
  // /collections/all/products.json?limit=50 — a 45KB download plus a 50-product JSON parse —
  // on EVERY page view, including visitors who never open the cart. Measured on the homepage:
  // 45KB of 822KB total, for markup sitting inside a closed drawer.
  // openCart() already calls initCartUpsell() each time the drawer opens (see initCartDrawer),
  // so nothing is lost by waiting; the upsell is still there the moment it can be seen.
  //
  // EXCEPT the cart page: sections/main-cart.liquid renders .cart-upsell--page inline, and that
  // one is visible immediately without anyone opening the drawer. Load it there, and only there.
  if (document.querySelector('.cart-upsell--page')) initCartUpsell();
});

// Export for section-specific use
window.RWDY = window.RWDY || {};
Object.assign(window.RWDY, {
  addToCart,
  showToast,
  toggleWishlist,
  isWishlisted,
  formatMoney,
  initCountdown,
  initCarousel,
  initStockIndicator,
});
